import React from 'react';

type ExtractedKeys<S extends string> = S extends `${string}{{${infer Key}}}${infer Rest}`
  ? Key | ExtractedKeys<Rest>
  : never;

/**
 * Replaces {{placeholder}} style placeholders in a string with strings or React components.
 * This function is type-safe and will infer required parameters from the message string.
 *
 * @example
 * formatMessage('Valikoima {{name}} on lisätty.', { name: 'Valikoima 1' });
 * // => 'Valikoima Valikoima 1 on lisätty.'
 *
 * @example
 * // Supports React components
 * import Link from '@mui/material/Link';
 * formatMessage('Valikoima {{name}} on lisätty.', { name: <Link href="#">Valikoima 1</Link> });
 * // => ['Valikoima ', <Link href="#">Valikoima 1</Link>, ' on lisätty.']
 */
export function formatMessage<S extends string>(
  message: S,
  params: Record<ExtractedKeys<S>, string>
): string;
export function formatMessage<S extends string>(
  message: S,
  params: Record<ExtractedKeys<S>, string | React.ReactNode>
): string | React.ReactNode[];
export function formatMessage<S extends string>(
  message: S,
  params: Record<ExtractedKeys<S>, string | React.ReactNode>
): string | React.ReactNode[] {
  // The implementation remains the same. The magic is in the function signature.
  if (!message) {
    return '';
  }

  // Find all placeholder matches
  const regex = /{{(.*?)}}/g;
  const matches = [...message.matchAll(regex)];

  // If there are no placeholders or no params, return the original message
  // The type-safety ensures params are present if there are matches, but we keep this for runtime safety.
  if (matches.length === 0 || !params) {
    return message;
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const [i, match] of matches.entries()) {
    const key = match[1];
    // We can now be more confident that `key` is in `params` because of our types.
    const value = (params as Record<string, string | React.ReactNode>)[key!];
    const index = match.index!;

    // Push the text segment before the placeholder
    if (index > lastIndex) {
      result.push(message.substring(lastIndex, index));
    }

    let replacement = value !== undefined ? value : match[0];

    // If the replacement is a valid React element that doesn't already have a key, clone it to add one.
    if (React.isValidElement(replacement) && replacement.key === null) {
      replacement = React.cloneElement(replacement, { key: `i18n-${key}-${i}` });
    }

    result.push(replacement);

    lastIndex = index + match[0].length;
  }

  // Push any remaining text after the last placeholder
  if (lastIndex < message.length) {
    result.push(message.substring(lastIndex));
  }

  // If all resulting parts are strings or numbers, we can join them to a single string.
  // Otherwise, we must return an array for React to render.
  const allStringLike = result.every(
    (item) => typeof item === 'string' || typeof item === 'number'
  );

  if (allStringLike) {
    return result.join('');
  }

  return result;
}

const LOCALE_FIN = {
  _CAMPAIGNS: 'kampanjat',
  _COLORS: 'värit',
  _EDIT_COLUMN_PRESET_COLUMNS: '-mallin sarakkeita ja niiden järjestystä',
  _IMAGES: 'kuvat',
  _MAINTENANCES: 'huollot',
  _MEDIA_WAITING_CATEGORISATION: 'lisätyt kuvat, jotka odottavat järjestämistä',
  _MESSAGES: 'viestit',
  _PRODUCT_PRICING: '-tuotteen hinnoittelua',
  _PRODUCTS: 'tuotteet',
  _ROWS: 'rivit',
  _SEARCHES: 'haut',
  _ORDERS: 'tilaukset',
  TEKSO_GROUP_OVERRIDE: 'Tekso-ryhmän ohitus',
  'ALL-CAMPAIGNS': 'Kaikki',
  'NEW-CAMPAIGNS': 'Aktiiviset ja tulevat',
  'NO-SEASON': 'Ei sesonkia',
  'PAST-CAMPAIGNS': 'Päättyneet',
  404: '404',
  403: '403',
  ABOUT_TO_CHANGE_CAMPAIGN: 'Olet vaihtamassa kampanjan',
  ACCEPT_SALES_ORDER: 'Olen tarkistanut ja hyväksyn tilauksen',
  ACCEPT_THE_ORDER_FIRST: 'Hyväksy tilaus ensin',
  ACCEPT_OK: 'OK',
  ACCEPTED: 'Hyväksytty',
  ACCEPTED_PENDING: 'Odottaa hyväksymistä',
  ACCEPTED_AUTOMATICALLY: 'hyväksytty automaattisesti',
  ACCEPTED_AUTOMATICALLY_FUTURE: 'hyväksytään automaattisesti',
  accepted: 'Tuotetiedot hyväksytty',
  acceptedMarketing: 'Hyväksytty markkinointiin',
  acceptedSuplier: 'Hyväksytty tuotetoimittaja',
  ACTIVE_SEARCH_FILTERS: 'Aktiiviset hakuehdot',
  ADD_ASSORTMENT_FOR_BRAND: 'Lisää uusi valikoima brändille',
  ADD_NOS_ASSORTMENT_FOR_BRAND: 'Lisää uusi täydennysvalikoima brändille',
  ADD_ASSORTMENT: 'Lisää valikoima',
  ADD_CAMPAIGN_AND_PHOTO_COLOR: 'Lisää kampanja ja kuvausväri',
  ADD_CAMPAIGN_TO_COLORS: 'Lisää kampanja värille',
  ADD_COMPONENT: 'Lisää lajitelma',
  ADD_DEFAULT_COPONENT_FOR_PRODUCT: 'Lisää lajitelma tuotteelle',
  ADD_DISCOUNT_TYPE: 'Lisää hinnoittelutyyppi',
  ADD_IMAGE: 'Lisää kuva',
  ADD_IMAGES_AND_VIDEOS_FROM_FILE: 'Lisää kuvia ja videoita tiedostosta (max. 10kpl / 30MB)',
  ADD_IMAGES_AND_VIDEOS: 'Lisää kuvia ja videoita',
  ADD_IMAGES_FROM_FILE: 'Lisää kuvia tiedostosta (max. 10kpl / 30MB)',
  ADD_NEW_COLOR: 'Lisää uusi väri',
  ADD_NEW_DEFAULT_COMPONENT: 'Lisää uusi oletuslajitelma',
  ADD_NEW_FILTER: 'Lisää uusi hakuehto',
  ADD_NEW_ROW: 'Lisää uusi rivi',
  ADD_NEW: 'Lisää uusi',
  ADD_ONE_OR_MORE_FILTERS: 'Lisää yksi tai useampia hakuehtoja',
  ADD_ORDER_AMOUNTS_FIRST: 'Lisää ensin tilausmäärät',
  ADD_PRICE: 'Lisää hinta',
  ADD_PRICING: 'Lisää hinnoittelutyyppi',
  ADD_PRODUCT_LABEL: 'Lisää materiaali tai tuotantotapa',
  ADD_PRODUCT: 'Lisää tuote',
  ADD_PRODUCTS: 'Lisää tuotteita',
  ADD_PRODUCT_CAMPAIGNS: 'Valitse kampanjat',
  ADD_PRODUCTS_TO_ASSORTMENT: 'Lisää tuotteita valikoimaan',
  ADD_TECHNOLOGY: 'Lisää teknologia',
  ADD_TO_ASSORTMENT_COMBINED: 'Yhdistä ja lisää valikoimaan',
  ADD_TO_ASSORTMENT_DIALOG_EAN_ERROR: 'Virhe EAN haussa',
  ADD_TO_ASSORTMENT_DIALOG_ERROR_EAN_BRAND: 'SAP variaation brändi ei vastaa valikoimaa',
  ADD_TO_ASSORTMENT_DIALOG_ERROR_EAN_PRODUCTS: 'EAN koodeilla löytyi useampi SAP tuote',
  ADD_TO_ASSORTMENT_DIALOG_FOOTER_HELP_TEXT: 'Valitse jokin annetuista tuotevaihtoehdoista',
  ADD_TO_ASSORTMENT_DIALOG_TITLE: 'Valitse valikoimaan lisättävät tuotteet',
  ADD_TO_ASSORTMENT: 'Lisää valikoimaan',
  ADD_TO_ASSORTMENT_DISABLED_TOOLTIP:
    'Voit lisätä vain yhden tuotteen kerrallaan. Kaikilla valituilla väreillä tulee olla sama valmistajan mallinumero.',
  ADD_TO_CART: 'Lisää ostoskoriin',
  ADD_VIDEOS: 'Lisää videoita',
  ADD_WAREHOUSE: 'Lisää toimitusvarasto',
  ADD_WARRANTY: 'Lisää takuu',
  ADD: 'Lisää',
  ADDED_TO_CART: 'lisätty ostoskoriin',
  ADDING_ASSORTMENT_BASED_ON_SELECTIONS: 'Valintojesi perusteella lisätään uusi valikoima:',
  ADDITIONAL_INFO_POSSESSIVE: 'lisätietoja',
  ADDITIONAL_INFO: 'Lisätiedot',
  ADDRESS: 'Osoite',
  AFFILIATE_PRODUCT: 'Kumppanikampajatuote',
  ALL_BRANDS: 'Kaikki brändit',
  ALL_COLORS: 'Kaikki värit',
  ALL_ESTABLISHED: 'Kaikki perustettu',
  ALL_PRODUCTS: 'Kaikki tuotteet',
  ALL_SAP_IDS: 'Kaikki SAP-id:t',
  ALL_SHOPS: 'Kaikki kaupat',
  ALL: 'Kaikki',
  ALLOW_WHOLESALE: 'Salli tuotteen myynti seurakaupassa',
  ALV_0: 'alv. 0\u00A0%',
  // ALV_DEFAULT: `alv. ${defaultVatFormatted}\u00A0%`,
  ALV_DEFAULT: 'alv.',
  ALV_INCLUDED: 'sis. alv.',
  ALV: 'alv.',
  ALV_255: 'alv. 25,5\u00A0%',
  AMOUNT_TO_BE_ORDERED: 'Tilattava määrä',
  AMOUNT: 'Määrä',
  ARCHIVE_SALES_ORDER: 'En aio tilata, siirrä arkistoon',
  AS_COLORS_NOK: 'Valitse värit',
  AS_EAN_IDS_NOK: 'Hae ID:t',
  AS_EANS_NOK: 'Valitse koot',
  AS_UPDATE_PRICAT_NOK: 'Päivitä Pricat',
  AS_PRICAT_NOK: 'Pricat-virheitä',
  AS_PRICES_NOK: 'Lisää hinnat',
  AS_SAP_UPDATE_NOK: 'Päivitä SAP',
  ASSORTMENT_ALREADY_EXISTS:
    'Samanlainen valikoima on jo olemassa. Lisää uusi lisätunniste erottamaan valikoimia.',
  ASSORTMENT_COPY_SUCCESS: 'Valikoima {{name}} on lisätty.',
  ASSORTMENT_COPY_ERROR: 'Valikoiman kopiointi epäonnistui',
  ASSORTMENT_COPY_MISSING_VARIANTS: 'Valikoiman kopiointi epäonnistui, variantteja ei löydetty',
  ASSORTMENT_MANAGEMENT: 'Valikoimanhallinta ja tilaukset',
  ASSORTMENT_ADDED: 'Valikoima {{name}} on lisätty.',
  ASSORTMENT_DELETED: 'Valikoima {{name}} on poistettu.',
  ASSORTMENT_ADDED_SUFFIX: '-valikoima on perustettu.',
  ASSORTMENT_BASIC_AND_UPDATE_INFO:
    'Valikoiman perustiedot ja tuotteiden päivityksessä huomioitavaa',
  ASSORTMENT_BASIC_INFO: 'Valikoiman tausta- ja lisätiedot',
  ASSORTMENT_CATEGORY_HELP_TEXT_BOLD: 'kategorian ja attribuutin',
  ASSORTMENT_CATEGORY_HELP_TEXT_SUFFIX: 'tuotteita.',
  ASSORTMENT_CATEGORY_HELP_TEXT: 'Lisää kategoriavalinta, kun valikoima sisältää erittäin rajatun',
  ASSORTMENT_COMPONENT: 'Lajitelma',
  ASSORTMENT_COPY: 'Kopioi valikoima',
  ASSORTMENT_CREATED_AT: 'Valikoima perustettu',
  ASSORTMENT_CREATED_BY: 'Valikoiman perustaja',
  ASSORTMENT_EXTRA_INFO_EXAMPLE:
    'Esimerkki lisätunnnisteesta: Tekniset paidat. Sallitut merkit: isot ja pienet kirjaimet (a-ö), numerot (0-9), välilyönti ja väliviiva (-).',
  ASSORTMENT_EXTRA_INFO_HELP_TEXT_BOLD:
    'sesongille, joka sisältää jo saman tuotetyypin valikoiman.',
  ASSORTMENT_EXTRA_INFO_HELP_TEXT:
    'Kirjoita valikoimalle tunniste, kun haluat lisätä uuden valikoiman',
  ASSORTMENT_ID: 'Valikoiman tunniste',
  ASSORTMENT_LEAD: 'Ketjun valikoimapäällikkö',
  ASSORTMENT_LOCKED_AT: 'Valikoima lukitaan brändin edustajalta',
  ASSORTMENT_UPDATED: 'Valikoimaa muokattu',
  ASSORTMENT_LAST_UPDATE: 'Viimeisin muokkaus',
  ASSORTMENT_LOCK_ACTION: 'Lukitse valikoima muutoksilta',
  ASSORTMENT_PRODUCT_INFO_STATUS: 'Tuotetiedot',
  ASSORTMENT_PRODUCT_INFO_OK: 'Tiedot kunnossa',
  ASSORTMENT_PRODUCT_INFO_ERROR: 'Tiedot ei kunnossa',
  ASSORTMENT_PRODUCT_TEKSO_GROUP_MISSING_ERROR:
    'Tekso-tuoteryhmä {{number}} puuttuu. Ota yhteys ylläpitoon.',
  EDIT_ASSORTMENT_PRODUCT_FAILED: 'Tallennus epäonnistui: {{message}}',
  ASSORTMENT_PRODUCTS_TO_BE_FIXED: 'Korjattavia tuotteita',
  ASSORTMENT_DURATION: 'Valikoima voimassa',
  ASSORTMENT_PRODUCT_COLORS_DIALOG_INFO: 'Lisää ja poista tuotteelle kiinnitettyjä värejä.',
  ASSORTMENT_PRODUCT_COLORS_DIALOG_TITLE: 'Kiinnitä värejä tuotteelle',
  ASSORTMENT_PRODUCT_MASS_CAMPAIGN_DIALOG_TITLE:
    'Kiinnitä kampanjoita väreille ja lisää kuvausväritiedot',
  ASSORTMENT_PRODUCT_REGULATIONS: 'Vastuullisuussäädökset',
  ASSORTMENT_PRODUCT_REGULATION_CBAM: 'Hiilirajamekanismi (CBAM)',
  ASSORTMENT_PRODUCT_REGULATION_DEFORESTATION: 'EUDR - Metsäkato',
  ASSORTMENT_PRODUCT_REGULATION_ACCEPT: 'Hyväksy säädökset',
  ASSORTMENT_PRODUCT_REGULATIONS_DIALOG_TITLE: 'Vastuullisuussäädöksien hyväksyntä',
  ASSORTMENT_PRODUCT_REGULATION: 'Vastuullisuussäädös',
  ASSORTMENT_PRODUCT_REGULATION_ROLE: 'Rooli',
  ASSORTMENT_PRODUCT_REGULATION_PERSON: 'Vastuuhenkilö',
  ASSORTMENT_PRODUCT_REGULATION_STATUS: 'Hyväksynnän status',
  ASSORTMENT_PRODUCT_REGULATION_DATE: 'Päivämäärä',
  ASSORTMENT_PRODUCT_REGULATIONS_DIALOG_DESCRIPTION:
    'Säädöksiin tutustuminen ja niiden hyväksyminen on pakollinen tuotekohtainen tieto. Jotta valikoimasta voidaan luoda tilaus, on kaikkien valikoimaan tilauksen luomishetkellä kiinnitettyjen tuotteiden vastuullisuussäädökset oltava hyväksytty.',
  ASSORTMENT_PUBLISH: 'Julkaise valikoima',
  ASSORTMENT_PUBLISHED_AT: 'Julkaistu',
  ASSORTMENT_PUBLISHED: 'Valikoima julkaistu',
  ASSORTMENT_STATUS_NOK: 'Valikoimassa virheitä',
  ASSORTMENT_STATUS_OK: 'OK',
  ASSORTMENT_STATUS: 'Valikoiman status',
  ASSORTMENT_SUPERVISORS: 'Valikoiman vastuuhenkilöt',
  ASSORTMENT_TAX_CLASS: 'Valikoiman veroluokitus',
  ASSORTMENT_TYPE: 'Valikoiman tyyppi',
  ASSORTMENTS: 'Valikoimat',
  ASSORTMENTS_DEFAULT_MENU: 'Ennakkotilattavat valikoimat',
  ASSORTMENTS_NOS_MENU: 'NOS-valikoimat',
  ASSORTMENT_NOS_TYPE: 'NOS-valikoiman tyyppi',
  ASSORTMENT_NOS_SEASONAL: 'NOS sesonki',
  ASSORTMENT_NOS_YEAR_ROUND: 'NOS ympärivuotinen',
  ASSORTMENT_DELETE_DIALOG_TITLE: 'Vahvista valikoiman/valikoimien poistaminen',
  ASSORTMENT_DELETE_DIALOG_CONTENT:
    'Oletko varma, että haluat poistaa valitun valikoiman/valikoimat?',
  ASSORTMENT_DELETE_SUCCESS: 'Valikoima(t) on poistettu.',
  ASSORTMENT_SIZE: 'Valikoiman koko',
  ASSORTMENT_SIZE_OPEN: 'Avoin',
  ASSORTMENT_START_DATE: 'Valikoima alkaa',
  ASSORTMENT_END_DATE: 'Valikoima päättyy',
  ASSORTMENT_START_SHORT: 'Alkaa',
  ASSORTMENT_END_SHORT: 'Päättyy',
  ATTACHED_CAMPAIGNS: 'Kiinnitetyt kampanjat',
  ATTACHED_COLORS: 'Kiinnitetyt värit',
  ATTACHED_TO_CAMPAIGN: 'Kiinnitetyt',
  AT_TIME: 'klo',
  AUTH_DIALOG_DESCRIPTION:
    'Kutsu epäonnistui, sillä käyttäjäistuntosi on vanhentunut. Sinun tulee kirjautua uudelleen sisään jatkaaksesi palvelun käyttöä.',
  B24_CREATION_MESSAGE_ID: 'B24 sanoman id',
  B24_ID: 'B24-id',
  B24_PRODUCT_TABLE_ID: 'B24 Product -taulun ID',
  B24_PRODUCT_SEARCH: 'B24-tuotehaku',
  B24_STATUS: 'B24-status',
  B24_STATUS_NEW: 'Uusi',
  B24_STATUS_CREATED: 'Tilaus luotu',
  B24_STATUS_REGISTERED: 'Rekisteröity',
  B24_STATUS_CANCELLED: 'Peruutettu',
  B24_STATUS_SHIPPED: 'Toimituksessa',
  B24_STATUS_INVOICED: 'Laskutettu',
  B24_STATUS_RESEND: 'Lähetetään uudelleen',
  B24_STATUS_FAILED: 'Epäonnistui',
  B24_STATUS_INVALID: 'Virheellinen',
  BACK_HOME: 'Takaisin palveluun',
  BACK_TO_ASSORTMENT_PAGE: 'Valikoiman sivulle',
  BACK_TO_PRODUCT_SEARCH: 'Takaisin tuotehakuun',
  BACK_TO_PRODUCTLIST: 'Tuotteet',
  BACK_TO_SALES_ORDERS_SHOPS_PAGE: 'Kaupat sivulle',
  BACK_TO_PREVIOUS_STEP: 'Takaisin edelliseen vaiheeseen',
  BACK: 'Takaisin',
  BASE_UNIT: 'Perusmääräyksikkö',
  BATCH_ITEM: 'Myyntierä',
  BASIC_SELECTION: 'Perusvalikoima',
  BIKE_LEASING_ORDER_LIST_TITLE: 'Työsuhdepyörätilaukset',
  BIKE_LEASING_ORDER_PAGE_TITLE: 'Työsuhdepyörätilaus',
  BIKE_LEASING_ORDER_LIST_FILTER_START_DATE: 'Tilaukset alkaen',
  BIKE_LEASING_ORDER_LIST_FILTER_END_DATE: 'Tilaukset päättyen',
  BIKE_LEASING_ORDER_LIST_FILTER_OPERATOR: 'Operaattori',
  BIKE_LEASING_ORDER_LIST_FILTER_SHOP: 'Kauppa',
  BIKE_LEASING_ORDER_LIST_FILTER_ORDER_STATUS: 'Tilauksen status',
  BIKE_LEASING_ORDER_BASIC_INFO_TITLE: 'Työsuhdepyörätilauksen kohde ja lisävarusteet',
  BIKE_LEASING_ORDER_CONTRACT_INFO_TITLE:
    'Työsuhdepyöräsopimuksen kaupan, sopimusajan ja operaattorin tiedot',
  BIKE_LEASING_ORDER_DETAILS: 'Käyttäjän antamia lisätietoja tilauksesta',
  BIKE_LEASING_BIKE_TYPE: 'Pyörän tyyppi',
  BIKE_LEASING_FRAME_NUMBER: 'Runkonumero',
  BIKE_LEASING_NORMAL_PRICE: 'Normaali hinta',
  BIKE_LEASING_PURCHASE_PRICE: 'Ostohinta tilauksen sopimushetkellä',
  BIKE_LEASING_PURCHASED_ACCESSORIES: 'Tilaukseen liitetyt lisävarusteet',
  BIKE_LEASING_SHOP: 'Pyörän toimittava kauppa',
  BIKE_LEASING_DELIVERY_METHOD: 'Toimitustapa',
  BIKE_LEASING_LEASE_DURATION: 'Sopimusaika',
  BIKE_LEASING_LEASE_START_DATE: 'Sopimuksen alkamispäivämäärä',
  BIKE_LEASING_OPERATOR: 'Operaattori',
  BIKE_LEASING_OPERATOR_CLIENT_ID: 'Operaattorin asiakastunnus (tilauskoodi)',
  BIKE_LEASING_ORDER_SUMMARY: 'Tilauserittely ja kokonaissumma',
  BIKE_LEASING_ACCESSORIES: 'Valitut lisävarusteet',
  BIKE_LEASING_DELIVERY_PRICE: 'Toimituskulut',
  BIKE_LEASING_BIKE: 'Työsuhdepyörä',
  BIKE_LEASING_ORDER_TOTAL_PRICE: 'Tilauksen kokonaissumma',
  BIKE_LEASING_TOTAL_PRICE: 'Kokonaissumma',
  BIKE_LEASING_MONTHLY_PAYMENT: 'Kuukausierän hinta',
  BIKE_LEASING_MONTHS_SHORT: 'kk',
  BIKE_LEASING_CLIENT_INFO_TITLE: 'Tilaajan tiedot',
  BIKE_LEASING_CLIENT_FIRST_NAME: 'Tilaajan etunimi',
  BIKE_LEASING_CLIENT_LAST_NAME: 'Tilaajan sukunimi',
  BIKE_LEASING_CLIENT_PHONE: 'Tilaajan puhelinnumero',
  BIKE_LEASING_CLIENT_EMAIL: 'Tilaajan sähköposti',
  BIKE_LEASING_CLIENT_ADDRESS: 'Tilaajan osoite',
  BIKE_LEASING_CLIENT_POSTAL_CODE: 'Postinumero',
  BIKE_LEASING_CLIENT_CITY: 'Kaupunki',
  BIKE_LEASING_SHOP_NOTES_TITLE: 'Kaupan huomiot tilauksesta',
  BIKE_LEASING_SHOP_NOTES_LABEL: 'Kaupan muistiinpanot',
  BIKE_LEASING_SHOP_NOTES_HELPER_TEXT: 'Työsuhdepyöräsopimukseen liittyviä sisäisiä huomioita.',
  BIKE_LEASING_ADD_FRAME_NUMBER: 'Lisää runkonumero',
  BIKE_LEASING_FRAME_NUMBER_DIALOG_TITLE: 'Lisää pyörän runkonumero',
  BIKE_LEASING_ORDER_CHANGE_STATUS: 'Muuta tilauksen statusta',
  BIKE_LEASING_ORDER_STATUS_WAITING_CONFIRMATION: 'Odottaa hyväksymistä',
  BIKE_LEASING_ORDER_STATUS_WAITING_DELIVERY: 'Tilaus siirretty varastolle',
  BIKE_LEASING_ORDER_STATUS_DELIVERED: 'Toimitus kuitattu',
  BIKE_LEASING_ORDER_DATE: 'Tilauspäivämäärä',
  BIKE_LEASING_ORDER_NUMBER: 'Tilausnumero',
  BIKE_LEASING_ORDER_IMAGE: 'Kuva',
  BIKE_LEASING_ORDER_OPERATOR: 'Operaattori',
  BIKE_LEASING_ORDER_SHOP: 'Kauppa',
  BIKE_LEASING_ORDER_STATUS: 'Tilauksen status',
  BIKE_LEASING_ORDER_OPERATOR_CLIENT_ID: 'Työsuhdepyöräkumppanin numero',
  BIKE_LEASING_ORDER_BILLING_STATUS: 'Laskutuksen status',
  BIKE_LEASING_ORDER_STATUS_NEW: 'Uusi',
  BIKE_LEASING_ORDER_STATUS_WAITING_FOR_APPROVAL: 'Odottaa hyväksymistä',
  BIKE_LEASING_ORDER_STATUS_APPROVED: 'Laskutusmääräys lähetetty',
  BIKE_LEASING_ORDER_STATUS_SENT_TO_WAREHOUSE: 'Tilaus siirretty varastolle',
  BIKE_LEASING_ORDER_STATUS_SENT_TO_CUSTOMER: 'Toimitus kuitattu',
  BIKE_LEASING_ORDER_STATUS_CANCELLED: 'Peruutettu',
  BIKE_LEASING_BILLING_STATUS_NOT_BILLED: 'Ei laskutettu kumppanilta',
  BIKE_LEASING_BILLING_STATUS_INVOICE_SENT: 'Lasku lähetetty',
  BIKE_LEASING_BILLING_STATUS_INVOICE_PAID: 'Lasku maksettu',
  BIKE_LEASING_ORDER_COPY_DATA: 'Kopioi tiedot',
  BIKE_LEASING_ORDER_CHANGE_BILLING_STATUS: 'Muuta laskutuksen statusta',
  BIKE_LEASING_WEBSTORE: 'Verkkokauppa',
  BIKE_LEASING_ACCESSORIES_TITLE: 'Tilaukseen liitetyt lisävarusteet',
  BIKE_LEASING_PICKUP: 'Nouto myymälästä',
  BIKE_LEASING_ORDER_STATUS_HISTORY: 'Tilauksen statuksen historia',
  BIKE_LEASING_ORDER_EXPORT_DATA: 'Vie tiedot Exceliin',
  BIKE_LEASING_ORDER_COPY_DATA_SUCCESS: 'Tiedot kopioitu',
  BIKE_LEASING_STATUS_UPDATE_ERROR: 'Tilauksen statuksen päivitys epäonnistui. Yritä uudelleen.',
  BIKE_LEASING_ORDER_UPDATE_ERROR: 'Päivitys epäonnistui. Yritä uudelleen.',
  BIKE_LEASING_ORDER_WEBSTORE_ID: 'Verkkokaupan tilausnumero',
  BIKE_LEASING_ORDER_CANCEL: 'Peruuta tilaus',
  BIKE_LEASING_ORDER_CANCEL_CONFIRMATION: 'Oletko varma, että haluat peruuttaa tilauksen?',
  BIKE_LEASING_ORDER_CANCEL_DIALOG_TITLE: 'Peruuta tilaus',
  BIKE_LEASING_EXCEL_CLIENT_NUMBER: 'Asiakasnumero',
  BIKE_LEASING_EXCEL_CLIENT_NAME: 'Asiakkaan nimi',
  BIKE_LEASING_EXCEL_CLIENT_ADDRESS: 'Kerta-asiakkaan osoite',
  BIKE_LEASING_EXCEL_SERVICE_PRODUCT: 'Palvelutuote',
  BIKE_LEASING_EXCEL_BILLING_ACCOUNT: 'Kirjanpidon tili',
  BIKE_LEASING_EXCEL_QUANTITY: 'Määrä',
  BIKE_LEASING_EXCEL_SHOP_ID: 'Tulosyksikkö',
  BIKE_LEASING_EXCEL_PRODUCT_NAME: 'Rivitason teksti',
  BIKE_LEASING_EXCEL_COMPANY: 'Yritys',
  BIKE_LEASING_EXCEL_ORDER_INFO: 'Otsikkotason teksti',
  BIKE_LEASING_EXCEL_CLIENT_ID: 'Operaattorin asiakastunnus',
  BIKE_LEASING_EXCEL_FRAME_NUMBER: 'Runkonumero',
  BIKE_LEASING_EXCEL_CLIENT: 'Tilaaja',
  BIKE_LEASING_EXCEL_DELIVERY_DATE: 'Luovutospäivä',
  BIKE_LEASING_EXCEL_ORDER_NUMBER: 'Verkkokaupan tilausnumero',
  BIKE_LEASING_EXCEL_ALV0_PRICE: 'Summa alv 0 %',
  BIKE_LEASING_EXCEL_TOTAL_PRICE: 'Yhteensä sis. alv',
  BIKE_LEASING_COPY_EMPLOYEE_DETAILS: 'TYÖNTEKIJÄN TIEDOT',
  BIKE_LEASING_COPY_EMPLOYEE_FIRST_LAST_NAME: 'Etu- ja sukunimi',
  BIKE_LEASING_COPY_EMPLOYEE_ID: 'Työntekijätunnus',
  BIKE_LEASING_COPY_EMPLOYER_OFFICIAL_NAME: 'Työnantajan / yrityksen virallinen nimi',
  BIKE_LEASING_COPY_EMPLOYER_LOCATION: 'Toimipaikka',
  BIKE_LEASING_COPY_EMPLOYEE_PHONE: 'Puhelinnumero',
  BIKE_LEASING_COPY_EMPLOYEE_EMAIL: 'Sähköposti',
  BIKE_LEASING_COPY_CUSTOMER_ADDRESS: 'Asiakkaan osoite',
  BIKE_LEASING_COPY_CUSTOMER_POSTAL_CODE: 'Postinumero',
  BIKE_LEASING_COPY_CUSTOMER_CITY: 'Kaupunki',
  BIKE_LEASING_COPY_BIKE_SHOP: 'Pyöräliike',
  BIKE_LEASING_COPY_BIKE_DETAILS: 'PYÖRÄN TIEDOT',
  BIKE_LEASING_COPY_BIKE_BRAND_MODEL: 'Pyörän merkki ja malli',
  BIKE_LEASING_COPY_BIKE_FRAME_NUMBER: 'Runkonumero',
  BIKE_LEASING_COPY_BIKE_SIZE: 'Koko',
  BIKE_LEASING_COPY_BIKE_COLOR: 'Väri',
  BIKE_LEASING_COPY_BIKE_CATEGORY: 'Pyörän kategoria',
  BIKE_LEASING_COPY_BIKE_DELIVERY_DATE: 'Toimituspäivä',
  BIKE_LEASING_COPY_ACCESSORIES: 'LISÄVARUSTEET',
  BIKE_LEASING_COPY_ACCESSORY: 'Lisävaruste',
  BIKE_LEASING_COPY_ACCESSORY_TYPE: 'Lisävarusteen tyyppi',
  BIKE_LEASING_COPY_ACCESSORY_BRAND_MODEL: 'Merkki ja malli',
  BIKE_LEASING_COPY_PRICE_VAT: 'Hinta (sis. alv.)',
  BIKE_LEASING_COPY_TOTAL_ORDER: 'KOKONAISSUMMA',
  BOOKABLE_FROM_WEB: 'Varattavissa verkkopalvelusta',
  BRAND_ASSORTMENT_REPRESENTATIVE: 'Brändin valikoiman edustaja',
  BRAND_COLOR_CODE: 'Valmistajan värikoodi',
  BRAND_LEAD: 'Brändin edustaja',
  BRAND_LOGO: 'Brändin logo',
  BRAND_SEASON_ORDERS: 'Merkin tilaukset kaudelle',
  BRAND_ORDER_LIMIT: 'Toimittajan minimi (alv. 0%)',
  brand: 'Brändi',
  BREADCRUMBS_ROOT: 'Tuotehallinta',
  BROKEN_LINK_TEXT:
    'URL-osoite on vanhentunut. Päivitä tai jätä osoitekenttä tyhjäksi ja tallenna tiedot.',
  BROKEN_LINKS: 'päivitettävä URL-osoite',
  BUDGETSPORT_WAREHOUSE_HELPER_TEXT_PT1:
    'Hyödynnämme tavaran lähetyksessä oletuksena kaikkia Budget Sport -varastoja paitsi DCU2.',
  BUDGETSPORT_WAREHOUSE_HELPER_TEXT_PT2:
    'Jos haluat rajata lähetyksen vain tiettyihin varastoihin, valitse mieleisesi toimituspaikat alla olevaan listaan.',
  budgetsport: 'Budgetsport',
  BUY_PRICE: 'Sisäänostohinta',
  buyer: 'Ostaja',
  BUYIN_PRICE: 'sisäänostoh.',
  CAMPAIGN_TOOL: 'Kampanjatyökalu',
  CAMPAIGN_ADVANCE_MARKETING_DATE_IN_PAST: 'Ennakkomarkkinointi ei voi alkaa menneisyydessä',
  CAMPAIGN_ADVANCE_MARKETING_DATE_AFTER_STARTS:
    'Ennakkomarkkinointi ei voi alkaa kampanjan alkamisen jälkeen',
  CAMPAIGN_COLORS: 'Kampanjan värit',
  CAMPAIGN_DAYS: 'Kampanjapäiviä yht.',
  CAMPAIGN_ENDS: 'Kampanja päättyy',
  CAMPAIGN_ID: 'Kampanjan id',
  CAMPAIGN_NAME: 'Kampanjan nimi',
  CAMPAIGN_PRODUCT: 'Kampanjatuote',
  CAMPAIGN_STARTS: 'Kampanja alkaa',
  CAMPAIGN_TIME: 'Kampanjan ajankohta',
  CAMPAIGN_MISSING_REQUIRED_FIELDS: 'Vaadittu kenttä on tyhjä',
  CAMPAIGN_ERROR: 'Jotain meni pieleen',
  CAMPAIGN_NAME_ALREADY_EXISTS: 'Samanniminen kampanja on jo olemassa',
  CAMPAIGN_CANT_DELETE_ENDED: 'Päättyneitä kampanjoita ei voi poistaa',
  CAMPAIGN_CANT_DELETE_ALREADY_STARTED: 'Käynnissä olevia kampanjoita ei voi poistaa',
  CAMPAIGN_CANT_EDIT_ADVANCE_MARKETING: 'Ennakkomarkkinointia ei voi enää muokata',
  CAMPAIGN_CANT_EDIT_NAME: 'Kampanjan nimeä ei voi enää muokata',
  CAMPAIGN_END_DATE_LESS_THAN_STARTS:
    'Kampanjan päättymisaika ei voi olla ennen kampanjan alkamisaikaa',
  CAMPAIGN_START_DATE_MORE_THAN_ENDS:
    'Kampanjan alkamisaika ei voi olla kampanjan päättymisaikaa myöhemmin',
  CAMPAIGN_START_DATE_IN_PAST: 'Kampanjan alkamisaika ei voi olla menneisyydessä',
  CAMPAIGN_END_DATE_IN_PAST: 'Kampanjan päättymisaika ei voi olla menneisyydessä',
  campaign: 'Kampanja',
  CAMPAIGN: 'Kampanja',
  campaignPrice: 'Kampanja hinta',
  campaignPriceTarget: 'Kampanja hinta on',
  campaignPriceTest: 'Kampanja hinta test',
  CAMPAIGNS_CURRENT: 'Käynnissä olevat kampanjat',
  CAMPAIGNS_NEXT_3M: 'Tulevat 3kk kampanjat',
  CAMPAIGNS_PREV_3M: 'Menneet 3kk kampanjat',
  CAMPAIGNS_UPCOMING: 'Kaikki tulevat kampanjat',
  CAMPAIGNS: 'Kampanjat',
  CAMPAIGNS_PHOTO_COLORS: 'Kampanjan kuvausvärit',
  CAMPAIGN_COLOR_ROLE_TOP: 'Kampanjakärki',
  CAMPAIGN_COLOR_ROLE_TOP_DESCRIPTION:
    'Kampanjakärkituote on volyymituote, johon kohdistuu isoin asiakaskysyntä.',
  CAMPAIGN_COLOR_ROLE_BASIC: 'Kampanjatuote',
  CAMPAIGN_LEVEL_ROLES: 'Kampanjatason ohjausmerkit',
  CAMPAIGN_PRODUCTS_ADD_INFO: 'Tuotteet lisätään kampanjaan PIMin kautta.',
  CANCEL: 'Peruuta',
  ERRORS_IN_ORDER_ROWS: 'Peruutettuja tai virheellisiä tilausrivejä',
  CART: 'Ostoskori',
  CATEGORY_EXAMPLE_TEXT: 'Esimerkki kategoriasta: Supreme 2S Pro jääkiekkomailat',
  CATEGORY_FILTER_HELPER_TEXT:
    'Kategoria-filtteri aktivoituu käytettäväksi, kun tuotetyyppi-filtteriin on valittu jokin seuraavista arvoista: vaatteet, kengät, varusteet, elintarvikkeet.',
  category: 'Kategoria',
  CATEGORY: 'Kategoria',
  CHAIN_LEVEL_ORDER_AMOUNTS: 'Ketjutason tilausmäärät',
  CHAIN_MISMATCH_TITLE: 'Tämä resurssi kuuluu toiseen ketjuun',
  CHAIN_MISMATCH_DESCRIPTION:
    'Resurssi, jota yrität tarkastella, kuuluu eri ketjuun kuin mikä sinulla on aktiivisena. Haluatko vaihtaa ketjua?',
  CHAIN_PRESET: 'Ketjun sarakemalli',
  CHAIN_SEARCH: 'Ketjun haku',
  CHANGE_STORE_TO_CHECK: 'Vaihda tarkistettava kauppa',
  CHANGES: 'Muutoksia',
  CHANGES_IN_ORDER_ROWS: 'Muutoksia tilausriveissä',
  changesSaved: 'Muutokset tallennettu',
  CHARS: 'merkkiä',
  CHOOSE_CAMPAIGN_COLORS: 'Valitse värit, joille valitut kampanjat lisätään',
  CHOOSE_CAMPAIGNS: 'Valitse kiinnitettävät kampanjat',
  CLEAR_FILTERS: 'Poista hakuehdot',
  CLEAR_SELECTIONS: 'Tyhjennä valinnat',
  CLEAR: 'Tyhjennä',
  CLIENT_INFO: 'Asiakkaan tiedot',
  CLIENT_NUMBER: 'Asiakasnumero',
  CLIENT_REFERENCE: 'Asiakkaan viite',
  CLOSE_ALL_TABS: 'Sulje kaikki välilehdet',
  CLOSE: 'Sulje',
  CLOTHING_RANK: 'Vaatteet-taso',
  CLOTHING: 'Vaatteet',
  CM: 'cm',
  COLOR_CAMPAIGN_AS_PHOTO: 'Väri kampanjassa kuvausvärinä',
  COLOR_CODE: 'Valmistajan värikoodi',
  COLOR_IMAGES_AVAILABLE_DATE: 'Kuvat tulossa (pvm)',
  COLOR_IMAGES_AVAILABLE: 'Kuvat saatavilla tavarantoimittajalta',
  COLOR_INFO_MANUFACTURER: 'Valmistajan väritiedot',
  COLOR_NAME: 'Valmistajan värinimi',
  COLOR_POSSESIVE: 'värin',
  COLOR_PRICING: 'Värikohtainen hinnoittelu',
  COLOR: 'Väri',
  COLORS_AND_AVAILABILITY: 'Värit ja saatavuus',
  COLORS: 'Värit',
  COLUMN_PRESET_NAME: 'Sarakemallin nimi',
  COLUMN_PRESET: 'Sarakemalli:',
  COLUMNS_TO_SAVE: 'Tallennettavia sarakkeita:',
  COMBINE_NOT_ALLOWED: 'Tuotteiden yhdistäminen ei ole sallittu',
  COMBINED_PRODUCT: 'yhdistetty tuote',
  COMMON_ID: 'Yhteisnimike',
  COMPLETED: 'Täydennetty',
  COMPONENT_SAP_ESTABLISHMENT: 'Lajitelmat & SAP-perustaminen',
  COMPONENT: 'Lajitelma',
  COMPONENTS_CREATED_PARTIAL: 'Perustettu osittain',
  COMPONENTS_SAP_STATUS: 'Lajitelmat & SAP-perustaminen',
  CONFIRM: 'Vahvista',
  CONFIRMED: 'Vahvistettu',
  CONFIRMED_AMOUNT: 'Vahvistettu määrä',
  CONFIRMED_AMOUNT_CHANGED: 'Määrä muuttunut',
  CONFIRMED_DELIVERY: 'Vahvistettu toimitusaika',
  CONTAINS_PRICAT_ERRORS: 'Sisältää Pricat-virheitä',
  CONTINUE: 'Jatka',
  CONTINUE_TO_SELECT_COLORS: 'Jatka valitsemaan värit',
  CONTINUE_TO_SELECT_PHOTOCOLORS: 'Jatka valitsemaan kuvausvärit',
  CONTINUING_COLORS: 'joista jatkavia',
  CONTINUOUS_COLOR: 'Jatkava väri SAP:issa',
  CONTINUOUS_PRODUCT: 'Jatkava tuote',
  CONTINUOUS: 'Jatkava',
  COPIED_TO_CLIPBOARD: 'Kopioitu leikepöydälle',
  COPONENT_CONTENT_FOR_PRODUCT: 'Lajitelman sisältö tuotteelle',
  COPY_EAN_CODES: 'Kopioi avattavien kokojen EAN-koodit',
  COPY_JSON: 'Kopioi tiedot (JSON)',
  COPY_JSON_SAP: 'Kopioi SAP tiedot (JSON)',
  COPY_JSON_B24: 'Kopioi B24 tiedot (JSON)',
  COPY_PRODUCT_INFO: 'Kopioi tuotteen perustiedot',
  COPY_PRODUCT_NAME: 'Kopioi tuotteen nimeksi',
  COPY_SALES_ORDER_AMOUNTS: 'Kopioi suositukset',
  COPY_SNACKBAR_TEXT: 'Sisällöt kopioitu onnistuneesti',
  COPY_TO_DIFFERENT_PRODUCT: 'Kopioi toisen SAP-numeron värille',
  COPY: 'Kopioi',
  COUNTRY_AND_CUSTOMS_INFO: 'Alkuperämaa- ja tullitiedot',
  COUNTRY_OF_ORIGIN: 'Alkuperämaa',
  CREATE_COPY: 'Luo kopio',
  CREATE_NEW_PRODUCT: 'Perusta uusi tuote',
  CREATE_ORDER: 'Luo tilaus',
  CREATE_ORDER_CONFIRM: 'Oletko varma että haluat luoda tilauksen?',
  CREATE_ORDER_CONFIRM_NO_VENDOR_TEXT:
    'Valikoimasta ei voida luoda keskusvaraston EDI-tilausta, koska valikoimalle ei ole määritetty toimittajaa. Voit kuitenkin tehdä tilauksen ja luoda keskusvaraston EDI-tilauksen manuaalisesti.',
  CREATE_PDF: 'Luo pdf',
  CREATE_SAP_PRODUCT: 'Perusta tuote',
  CREATE: 'Perusta',
  CREATED_AT: 'Perustettu',
  CREATED: 'Luotu',
  CREATION_FAILED: 'Perustaminen epäonnistui',
  CREATION_SUCCEED: 'Perustaminen onnistui',
  CSV_SEPARATOR: 'Kolumnierotin',
  CURRENCY: 'Ostohinnan valuutta',
  CUSTOMS_NUMBER_ADDITIONAL_INFO: 'CN-koodi eli tullinimike on 10-numeroinen merkkisarja.',
  CUSTOMS_NUMBER: 'Tullinimike',
  CUT_SNACKBAR_TEXT: 'Sisällöt siirretty onnistuneesti',
  CUT_TO_DIFFERENT_PRODUCT: 'Siirä toisen SAP-numeron värille',
  DATA_UPDATED_TO_SAP: 'Tiedot päivitetty SAP:iin',
  DATE: 'Päivämäärä',
  DAYS: 'pv',
  DAYS_AGO: 'pv sitten',
  DC_ERROR_ALREADY_EXISTS:
    'Oletuslajitelma, jota olet lisäämässä, ei ole yksilöllinen. Vastaava lajitelma löytyy jo.',
  DC_ERROR_INVALID_REQUEST: 'Tallennus epäonnistui',
  DDP_RECIPIENT: 'DDP Vastaanottaja',
  DEADLINE: 'Määräaika',
  DEFAULT_COMPONENT_ADDED: 'Uusi oletuslajitelma lisätty',
  DEFAULT_COMPONENT_HELP_TEXT:
    'Kun valitset tuotteelle lajitelmaa, huomaathan, että oletuslajitelmat näytetään taulukossa vain niiden kokojen osalta, jotka on avattu tuotteelle SAP:issa. Oletuslajitelma voi siis sisältää muitakin kokoja kuin mitä taulukossa näet. Lajitelma luodaan tuotteelle, jota olet päivittämässä, kuitenkin vain niiden kokojen osalta, jotka ovat lajitelman valintahetkellä avattuna tuotteelle.',
  DEFAULT_COMPONENT_SUFFIX: '-oletuslajitelma',
  DEFAULT_RANKS: 'Oletustasot',
  DEFAULT: 'oletus',
  DELETE_CAMPAIGN: 'Poista kampanja',
  DELETE_COLOR: 'Poista väri',
  DELETE_ORDER_CONFIRM_B24:
    'Oletko varma että haluat poistaa tilauksen tiedot? Tilauksen tiedot pitää poistaa manuaalisesti myös SAP:ista ja B24:stä',
  DELETE_ORDER_CONFIRM:
    'Oletko varma että haluat poistaa tilauksen tiedot? Tilauksen tiedot pitää poistaa manuaalisesti myös SAP:ista.',
  DELETE_ORDER_INFO: 'Poista tilauksen tiedot',
  DELETE_PRICAT_PRODUCTS: 'Poista tuotteiden Pricat-tiedot',
  DELETE_PRICAT_PRODUCTS_SUCCESS: 'Tuotteiden Pricat-tiedot on poistettu onnistuneesti',
  DELETE_PRICAT_PRODUCTS_ERROR: 'Tuotteiden Pricat-tietojen poisto epäonnistui',
  DELETE_PRICAT_PRODUCT_CONFIRM:
    'Oletko varma että haluat poistaa valittujen tuotteiden Pricat-tiedot?',
  DELETE_PRODUCT_CONFIRM_DESCRIPTION: 'Oletko varma että haluat poistaa tuotteen?',
  DELETE_PRODUCT_CONFIRM_TITLE: 'Poista tuote',
  DELETE_PRODUCT: 'Poista tuote',
  DELETE_CAMPAIGN_CONFIRM_DESCRIPTION: 'Oletko varma että haluat poistaa kampanjan?',
  DELETE_CAMPAIGN_PRODUCTS_CONFIRM_DESCRIPTION:
    'Olet poistamassa [X] tuotetta kampanjasta. Oletko varma että haluat poistaa tuotteet?',
  DELETE_CAMPAIGN_PRODUCT_CONFIRM_DESCRIPTION:
    'Olet poistamassa tuotteen kampanjasta. Oletko varma että haluat poistaa tuotteen?',
  DELETE_CAMPAIGN_CONFIRM_TITLE: 'Poista kampanja',
  DELETE_ROW: 'Poista rivi',
  DELETE_UNCATEGORISED_IMAGES: 'Poista kategorisoimattomat kuvat',
  DELETE: 'Poista',
  DELIVERY: 'Toimitus',
  DELIVERY_CONDITION: 'Toimitusehto',
  DELIVERY_CUSTOMER: 'Toimitusasiakas',
  DELIVERY_DATE: 'Toimituspäivä',
  DELIVERY_INFO: 'Toimittaja- ja toimitustiedot',
  DELIVERY_METHODS: 'Toimitustavat',
  DELIVERY_TIME: 'Toimitusaika',
  DELIVERY_TIME_ENDS: 'Toimitusaika päättyy',
  DELIVERY_TIME_STARTS: 'Toimitusaika alkaa',
  DELIVERY_WAREHOUSE: 'Toimitusvaraston rajaukset',
  DELIVERY_WEEKS: 'Toimitusviikot',
  DELIVERY_DAY_AND_YEAR: 'toimituspäivä ja vuosi',
  DELIVERY_DAY_SHORT: 'Toimituspv',
  DELIVERY_DAY: 'Toimituspäivä',
  DELIVERY_DAY_CHANGED: 'Toimituspäivä muuttunut',
  DELIVERY_DAYS: 'Tuotteen toimituspäivät',
  delivery: 'Toimitustapa',
  deliveryDates: 'Toimituspäivät',
  DESCRIPTION_OK: 'Kuvaus ok',
  description: 'Pitkä tuotekuvaus',
  DEV_ENVIRONMENT: 'Test',
  DISCOUNT_PERCENTAGE: 'Alennus %',
  DISCOUNT: 'Alennus',
  discountDays: 'Ale-päivät',
  PRODUCTS_DISTRIBUTION_PROFILE: 'Tuotteiden toimituskulku',
  DISTRIBUTION_PROFILE: 'Tuotteen toimituskulku',
  DISTRIBUTION_PROFILE_ZVA: 'Hyllytys (ZVA)',
  DISTRIBUTION_PROFILE_ZLV: 'Läpivirtaus (ZLV)',
  DO_NOT_SHOW_AGAIN: 'Älä näytä tätä viestiä uudelleen.',
  DONT_SAVE: 'Ei tallenneta',
  DOWNLOAD_FILE: 'Lataa tiedosto',
  INCLUDE_ZERO_LINES_IN_REPORT: 'Sisällytä nollarivit raporttiin',
  DOWNLOAD_CSV: 'Lataa .CSV-tiedosto',
  DOWNLOAD_IMAGES: 'Lataa kuvat',
  DOWNLOAD_TEXT: 'Lataa tekstit',
  DOWNLOAD_PRICES: 'Lataa hintatiedot',
  DUPLICATE_FILTER: 'Monista hakuehto',
  DUPLICATE_SAP_IDS: 'Duplikaatit SAP-id:t',
  DUPLICATE: 'Duplikaatti',
  EAN_AMOUNT: 'EAN kappalemäärä',
  EAN_CODES: 'EAN koodit',
  EAN_CODE: 'EAN-koodi',
  EAN_INFO: 'EAN-, koko- ja määrätiedot',
  EAN_NUMBER: 'EAN-koodi',
  EAN_NUMBERS_COPIED: 'EAN-koodit kopioitu',
  EDIT_ASSORTMENT_PRODUCT_ADDITIONAL_INFO_SUFFIX: '-tuotteen lisätietoja',
  EDIT_ASSORTMENT_PRODUCTS_ORDER: 'Muokkaa tuotteiden järjestystä',
  EDIT_BRAND_COLOR_CODE: 'Muokkaa valmistajan värikoodi',
  EDIT_CAMPAIGN_PHOTO_COLORS: 'Muokkaa kampanjan kuvausvärejä',
  EDIT_COLOR_CAMPAIGN_PREFIX: 'Muokkaa värin',
  EDIT_COLOR_CAMPAIGN_SUFFIX: 'kampanja- ja kuvausväritietoa',
  EDIT_COLORS: 'Muokkaa värejä',
  EDIT_COLUMN_PRESET: 'Muokkaa sarakkeita',
  EDIT_COLUMNS_MAIN_TEXT:
    'Valitse taulukossa näytettävät ominaisuudet ja muokkaa halutessasi niiden järjestystä.',
  EDIT_COLUMNS: 'Muokkaa sarakkeita',
  EDIT_IMAGE_SAP_IDS: 'Muokkaa tietoa kuvaan merkityistä muista tuotteista',
  EDIT_PRICING: 'Muokkaa hinnoittelutyyppi',
  EDIT_PRODUCT_COLORS_PREFIX: 'Muokkaa tuotteen',
  EDIT_PRODUCT_COLORS_SUFFIX: 'värejä',
  EDIT_PRODUCT_KW_NUMBER: '-tuotteen kw-koodia',
  EDIT_PROPERTY: 'Muokkaa ominaisuutta',
  EDIT_SALES_ORDER_FAIL: 'Tilauksen muokkaus epäonnistui',
  EDIT_SALES_ORDER_SHOP_ORDER_FAIL: 'Kaupan tilauksen muokkaus epäonnistui',
  EDIT_SALES_ORDER_SHOP_ORDER_SUCCESS: 'Kaupan tilaus onnistuneesti muokattu',
  EDIT_SALES_ORDER_SUCCESS: 'Tilaus onnistuneesti muokattu',
  EDIT_SEARCH: 'Muokkaa hakua',
  EDIT_SEASON_COLORS: 'Muokkaa sesongin värejä',
  EDIT_SELECTED: 'Muokkaa valittuja',
  EDIT: 'Muokkaa',
  EDITED_SOURCE: 'Muokannut',
  EDITED: 'Muokattu',
  EMAIL: 'Sähköpostiosoite',
  ENV_SELECTOR_CHANGE_TO_DEV: 'Vaihda testidataan',
  ENV_SELECTOR_CHANGE_TO_PROD: 'Vaihda tuotantodataan',
  EQUIPMENT_RANK: 'Varusteet-taso',
  EQUIPMENT: 'Varusteet',
  ERROR: 'Virhe',
  ERROR_IN_ADDING_PRODUCT_PREFIX: 'Virhe tuotteen',
  ERROR_IN_ADDING_PRODUCT_SUFFIX: 'lisäämisessä',
  ERROR_UPDATE_TITLE: 'Tekemäsi muutos ei tallentunut',
  ERROR_SOMETHING_WENT_WRONG:
    'Jotain meni pieleen. Varmista, että nettiyhteytesi on kunnossa ja yritä uudelleen.',
  ERROR_PART_UPDATE_TITLE: 'Osa syöttämästäsi tiedoista ei tallentunut',
  ERROR_PART_UPDATE_RELOAD_PAGE:
    'Lataa sivu uudelleen, etsi puutteelliset tiedot ja päivitä ne. Varmista, että nettiyhteytesi on kunnossa.',
  ERROR_SAVING_DELIVERY_DATES: 'Virhe toimituspäivien tallennuksessa',
  ESTABLISHED: 'perustettu',
  ESTIMATED_START_DATE: 'Arvioitu aloituspvm',
  ESTIMATED_TIME: 'Arvioitu ajankohta',
  EVENT_NAME: 'Tapahtuma',
  EVENT_TIME: 'Aika',
  EVENT_USER: 'Käyttäjä',
  EXAMINE_ORDER: 'Tarkastele tilausta',
  EXCEPTIONAL_POTENTIAL: 'Erinomainen',
  EXISTING_COLOR: 'Olemassa oleva väri',
  EXISTING_EAN_CODES: 'Olemassa olevat EAN-koodit',
  EXISTING_KW_NUMBER: 'Olemassa oleva tuotekoodi',
  EXISTING_VARIANT: 'Olemassa oleva koko',
  exclusive: 'Exclusive tuote',
  EXPORT: 'Vie',
  EXPORT_TO_TABLE: 'Vie taulukkoon',
  FAILED_PRODUCTS: 'Epäonnistuneet tuotteet',
  FAILED: 'Epäonnistunut',
  FAULTY_AMOUNTS: 'Virheelliset määrät',
  FEDAS_CODE: 'Fedas-koodi',
  FIELD_VALIDATION_ERROR: 'Tarkista kentän muotoilu.',
  FILE_NAME: 'Tiedoston nimi',
  FILE_TYPE: 'Tiedostotyyppi',
  FILLED: 'täytetty',
  FILTERS_TO_SAVE: 'Tallennettavia hakuehtoja:',
  FIRST_DELIVERYWEEK_SHORT: 'Ensisij. toimitusvko',
  FIRST_NAME: 'Etunimi',
  FIRST_PAGE: 'Ensimmäinen sivu',
  FIX_LATER: 'Korjaa myöhemmin',
  FIX: 'Korjaa',
  FIX_ERRORS: 'Korjaa virheet',
  FIXED_PRICE: 'Kiinteä hinta',
  FOOD: 'Elintarvikkeet',
  FOCUS_PRODUCT: 'Must Win -tuote',
  FOR_PRODUCTS: 'tuotteille',
  FOR_PRODUCT: 'tuotteelle',
  FROM_WEEK: 'alkaen vk',
  GENERAL_ITEM_CATEGORY_GROUP: 'Tuotteen toimituskulku',
  GOOD_POTENTIAL: 'Hyvä',
  HAS_BALANCE: 'Saatavilla varastossa',
  HIDE_FROM_ORDER: 'Piilota tilaukselta',
  HIDE_VAT: 'Piilota alv.',
  HIDE: 'Piilota',
  HIDE_PRICAT_ERRORS: 'Piilota Pricat-virheilmoitukset käyttöliittymästä',
  ERROR_HIDING_PRICAT_ERRORS: 'Virhe Pricat-virheiden piilottamisessa',
  HIGHEST_PRICE: 'Korkein hinta',
  HISTORY: 'Historia',
  HOUR_SHORT: 'h',
  IIC_PRODUCT_IMAGE: 'Tuotteen kuvat',
  IMAGE_COLOR_ACCEPT: 'Hyväksy kuvien väriprofiili ja koko, ja poista varoitukset',
  IMAGE_COLOR_PROFILE_DIALOG_CONTENT_AFTER:
    ' merkityt kuvat toivotussa väriprofiilissa ja riittävän suurikokoisina.',
  IMAGE_COLOR_PROFILE_DIALOG_CONTENT_PRE:
    'Yhdessä tai useammassa ladatussa kuvassa havaittiin ongelmia väriprofiilin tai kuvan koon kanssa. Väriprofiilin tulisi vastata sRGB-profiilia, ja kuvan leveyden tulisi olla vähintään 750px. Ongelma esiintyy joko valittuna olevalla värillä, tai jollakin toisella tuotteen värillä. Ole hyvä ja lataa uudelleen ikonilla ',
  IMAGE_COLOR_PROFILE_DIALOG_TITLE: 'Kuvilla vääriä väriprofiileita',
  IMAGE_COLOR_PROFILE_TOOLTIP:
    'Varmista, että kuvan väriprofiili vastaa sRGB-profiilia. Tarvittaessa lataa kuva uudelleen oikealla väriprofiililla, tai hyväksy kuvan profiili oikeaksi klikkaamalla Poista varoitus -linkkiä.',
  IMAGE_COLOR_PROFILE_WARNING: 'Tarkista väriprofiili',
  IMAGE_SIZE_WARNING: 'Tarkista kuvan koko',
  IMAGE_SIZE_WARNING_TOOLTIP:
    'Tuotekuvan on hyvä olla vähintään 750px leveä. Tarvittaessa lataa kuvasta isompi versio, tai ohita varoitus klikkaamalla Poista varoitus -linkkiä.',
  IMAGE_LIST_BREADCRUMBS: 'Kuvat',
  IMAGE_LIST_NO_RESULTS:
    'Antamillasi hakuehdoilla ei löytynyt yhtään tuotetta. Muokkaa hakua löytääksesi tuotteita.',
  IMAGE_SEARCH: 'Kuvahaku',
  IMAGE: 'Kuva',
  IMAGES_LIMIT_MESSAGE_1:
    'Kuvien latautuminen keskeytyi automaattisesti, sillä koitit tehdä kerralla liian suuren siirron.',
  IMAGES_LIMIT_MESSAGE_2:
    'Yritä uudelleen! Valitse enintään kymmenen kuvaa ja varmista, että siirron yhteenlaskettu koko on alle 50 MB.',
  IMAGES_LIMIT_MESSAGE_3: 'Kuvien latauduttua voit tehdä taas uuden siirron.',
  IMAGES_LIMIT_TITLE: 'Kuvien lataus keskeytyi.',
  IMAGES_OK: 'Kuvat ok',
  images: 'Kuvat',
  imagesInWarehouse: 'Kuvat (saldolliset)',
  IN_CART: 'ostoskorissa',
  IN_CENTRAL_WAREHOUSE: 'Keskusvarastossa',
  IN_TOTAL: 'Yhteensä',
  inCampaign: 'Tuote on aktiivinen',
  inCampaignId: 'Tuote on aktiivinen (id)',
  INITIAL_SELECT: 'Valitse...',
  INITIAL_TEXT_TOO_LONG:
    'HUOM! Teksti on liian pitkä ja se on lyhennetty kenttään automaattisesti. Sinun tulee tallentaa muutokset päivittääksesi tiedot.',
  INITIAL_TEXT: 'Alkuperäinen teksti:',
  INPUT_VALUE_REQUIRED: 'Arvo vaaditaan',
  inStock: 'Ketjusaldollinen',
  inStockWeb: 'VK saldollinen',
  INSTRUCTIONS: 'Ohjeet',
  INTERNAL_NOTES: 'Sisäiset muistiinpanot ja huomiot',
  INTERSPORT_WAREHOUSE_HELPER_TEXT_PT1:
    'Hyödynnämme tavaran lähetyksessä Intersport-verkkokaupan varastoa.',
  INTERSPORT_WAREHOUSE_HELPER_TEXT_PT2:
    'Jos haluat rajata lähetyksen muihin varastoihin, valitse mieleisesi toimituspaikat alla olevaan listaan.',
  intersport: 'Intersport',
  ITEMS_PER_CUSTOMER: 'kpl / asiakas',
  ITEMS: 'kpl',
  KG: 'kg',
  KW_NUMBER: 'Tuotekoodi',
  KW_NUMBR: 'KW-nro',
  kwNumber: 'KW Numero',
  LAST_3_PRICES: 'Viimeisimmät 3 hintaa',
  LAST_EDITED: 'Muokattu viimeksi',
  LAST_NAME: 'Sukunimi',
  LAST_ORDER_DATE: 'Tilaus jätettävä viimeistään',
  LAST_PAGE: 'Viimeinen sivu',
  LAST_SAVED_COLUMN_PRESETS: 'Viimeksi käytetyt tallennetut sarakemalit',
  LAST_SAVED_SEARCHES: 'Viimeisimmät tallennetut haut',
  lastChanceDays: 'Last chance päivät',
  LOAD_MORE_PRODUCTS: 'Lataa lisää tuotteita',
  LOADING: 'Ladataan',
  LOCK_STATUS: 'Lukituksen status',
  LOCK: 'Lukitse',
  loginAgain: 'Kirjaudu uudelleen',
  LOGOUT: 'Kirjaudu ulos',
  LONG_DESCRIPTION: 'Pitkä kuvaus',
  LOWEST_PRICE_30_DAYS: 'viimeisen 30 päivän alin hinta',
  LOWEST_PRICE: 'Alin hinta',
  MAIN_SPORT: 'Päälaji',
  MAINTENANCES: 'Huoltopalvelut',
  MAINTENANCE_BREAK: 'Käyttökatko',
  MAINTENANCE_BREAK_SERVICES: 'Seuraavat palvelut ovat katkon aikana suljettuina',
  MAINTENANCE_CAT_SERVICES_DIALOG_HELP:
    'Asiakkaan varatessa huoltopalvelua, varataan käytettävästä myymälä- ja kategoriakohtaisesta huoltoajasta palvelun kesto tämän taulukon mukaisesti.',
  MAINTENANCE_CAT_SERVICES: '-kategorian palvelut',
  MAINTENANCE_CLIENT: 'Asiakas',
  MAINTENANCE_DATE: 'Huollon päivämäärä',
  MAINTENANCE_DAY_STATUS: 'Varausten tilanne',
  MAINTENANCE_EMAIL_NOTICE: 'Asiakkaalle lähetetään myös ilmoitus huollon valmistumisesta',
  MAINTENANCE_ID: 'Huollon ID',
  MAINTENANCE_LAST_PICKUP_DATE_INFO: ', jonka jälkeen veloitus 10€/päivä',
  MAINTENANCE_LAST_PICKUP_DATE: 'Viimeinen noutopäivä',
  MAINTENANCE_LIST_SEARCH: 'Etsi huollon ID:llä',
  MAINTENANCE_NOTES_INFO:
    'Tähän voit kirjata esimerkiksi kuka suoritti huollon ja sen yhteydessä mahdollisesti esiin nousseet asiat.',
  MAINTENANCE_NOTES: 'Huollon muistiinpanot',
  MAINTENANCE_OPENED_SLOTS: 'Avatut huoltoajat (h)',
  MAINTENANCE_ORDERS: 'Huoltovaraukset',
  MAINTENANCE_READY_AT: 'Huolto valmis',
  MAINTENANCE_SERVICE_ERROR:
    'Huoltopalvelua ei voida avata, sillä käyttäjätunnukseesi ei ole yhdistetty myymälää. Ota yhteyttä järjestelmän ylläpitäjään.',
  MAINTENANCE_SERVICE: 'Huoltopalvelut',
  MAINTENANCE_SLOT_REVERT_NOTICE: 'Huolto perutaan ja aika vapautetaan uudelleen varattavaksi',
  MAINTENANCE_SLOTS_TITLE: 'Aikojen hallinta',
  MAINTENANCE_SLOTS: 'Aikojen hallinta',
  MAINTENANCE_STATUS: 'Huollon tilanne',
  MANAGE_TABS: 'Hallitse välilehtiä',
  MANUFACTURER_PRODUCT_CODE: 'Valmistajan tuotekoodi',
  MARK_AS_READ: 'Merkitse luetuiksi',
  MARK_AS_UNREAD: 'Merkitse lukemattomaksi',
  MASS_EDIT_CAMPAINGS: 'Valitse kampanjat',
  MASS_EDIT_DELIVERY_DAYS: 'Valitse toimituspäivät',
  MASS_EDIT_DELIVERY_DAYS_SUCCESS: 'Toimituspäivät päivitetty',
  MASS_EDIT_ASSORTMENT_DELIVERY_TYPE_DIALOG_TEXT:
    'Tämän toiminnallisuuden avulla muokkaat valikoiman kaikkien tuotteiden varastointitapaa. Päivityksen jälkeen voit tehdä tuotekohtaisia muutoksia tuotteiden taustatiedoissa.',
  MASS_EDIT_ROLES: 'Aseta tuoterooli',
  MASS_EDIT_ASSORTMENT_DELIVERY_TYPE: 'Muokkaa tuotteiden toimitustapaa',
  MASS_EDIT_ASSORTMENT_DISTRIBUTION_PROFILE: 'Muokkaa tuotteiden toimituskulkua',
  MASS_EDIT_PURCHASE_PRICE: 'Muokkaa tarjousnettohintaa',
  MASS_EDIT_SAP_NET_PRICE: 'Muokkaa ostohinnoittelun nettohintaa',
  MASS_SAP_ACTION_INFO: 'Tuotteet perustetaan / päivitetään SAP:iin taustalla.',
  MASS_SAP_UPDATE_CREATE: 'Perusta / Päivitä tuotteet SAP:iin',
  MASS_PRICAT_UPDATE: 'Päivitä Pricat-tiedot',
  MASS_PRICAT_UPDATE_INFO: 'Tuotteiden Pricat-tiedot päivitetään taustalla.',
  PURCHASE_PRICE: 'tarjousnettohinta',
  PURCHASE_PRICES: 'tarjousnettohinnat',
  PURCHASE_PRICE_S: 'tarjousnettohinnan',
  MASS_SAP_NET_PRICE: 'ostohinnoittelun nettohinta',
  MASS_SAP_NET_PRICES: 'ostohinnoittelun nettohinnat',
  MASS_SAP_NET_PRICE_S: 'ostohinnoittelun nettohinnan',
  PRICING: 'hinnoittelu',
  PRICING_TYPE_INDIVIDUAL: 'Tuotekohtainen alennushinta',
  PRICING_VISIBILITY_SK_OVH: 'Seurakauppa OVH',
  PRICING_VISIBILITY_SK_TEAMSHOP: 'Seurakauppa seurahinta',
  PRINT_MANAGEMENT: 'Tulostevelho',
  PRICAT_ID: 'Pricat ID',
  PRICAT_PRODUCT_UPDATE_FAIL: 'Pricat-päivitys epäonnistui',
  PRICAT_PRODUCT_UPDATE_SUCCESS: 'Pricat-päivitys onnistui',
  PRICAT_UPDATE_INVALID_FEDAS_CODE: 'Pricatin Fedas-koodia ei löydy järjestelmästä',
  PRICAT_UPDATED: 'Pricat-tiedot päivitetty',
  PRICAT_UPDATE_IN_PROGRESS: 'Pricat-tietoja päivitetään.',
  NOT_YET_AVAILABLE_IN_IS: ' (ei vielä käytössä Intersportilla)',
  MAX_ITEMS: 'Enint.',
  MAX: 'Enintään',
  mcSync: 'mcSync',
  MEDIA_ADD_VIDEO_DIALOG_TITLE: 'Lisää video Youtube- tai Vimeo-palvelusta linkillä tai ID:llä',
  MEDIA_COPY_DIALOG_HELPER_TEXT_PREFIX: 'Valitse väri, jolle kopioidaan kaikki',
  MEDIA_COPY_DIALOG_HELPER_TEXT_SUFFIX:
    '-värin kuvat ja videot. Kopiointi ei koske B24-palvelusta tallennettuja kuvia.',
  MEDIA_COPY_TITLE_PREFIX: 'Kopioi ja liitä värin',
  MEDIA_COPY_TITLE_SUFFIX: 'kuvat ja videot toiselle SAP-värille',
  MEDIA_CUT_DIALOG_HELPER_TEXT_PREFIX: 'Valitse väri, jolle siirretään kaikki',
  MEDIA_CUT_DIALOG_HELPER_TEXT_SUFFIX:
    '-värin kuvat ja videot. Siirto ei koske B24-palvelusta tallennettuja kuvia.',
  MEDIA_CUT_TITLE_PREFIX: 'Siirrä värin ',
  MEDIA_CUT_TITLE_SUFFIX: 'kuvat ja videot toiselle SAP-värille',
  MEDIA_DELETE_TITLE_PREFIX: 'Poista kaikki värille',
  MEDIA_DELETE_TITLE_SUFFIX: 'lisätyt kuvat ja videot',
  MEDIA_MANAGEMENT_HELPER_TEXT:
    'Järjestä kuvat raahaamalla ja pudottamalla ne johonkin alla näkyvistä kuvakategorioista; Tuotekuvat, Studiokuvat tai Fiiliskuvat.',
  MESSAGES_SEARCH_FILTER_BRAND: 'Brändi',
  MESSAGES_SEARCH_FILTER_END: 'Viesti kirjoitettu viimeistään',
  MESSAGES_SEARCH_FILTER_START: 'Viesti kirjoitettu aikaisintaan',
  MESSAGES_SEARCH_FILTER_USER: 'Viestin kirjoittaja',
  MESSAGES_SEARCH: 'Etsi viesteistä',
  MESSAGES: 'Viestit',
  MISSING_FROM_PRICAT: 'Puuttuu Pricat-sanomalta',
  DUPLICATE_COLOR_ERROR: 'Duplikaatteja värikoodeja',
  DUPLICATE_COLOR_NOK: 'Duplikaattivärejä',
  DUPLICATE_VARIANT: 'Duplikaattikoko',
  MISSING_CONVERSION:
    'Tuotteelle on lisätty yksi tai useampi kuva, mutta ensisijaisen kuvan esikatseluversiota ei voida vielä näyttää. Jotta saat esikatselukuvan näkyviin, odota hetki ja päivitä sivu.',
  MISSING_ORDER_SETTINGS_TOOLTIP: 'Täydennä ensin julkaisuun liittyvät asetukset',
  MISSING_IMAGE: 'Kuva puuttuu',
  MISSING_THUMBNAIL:
    'Tuotteelle on lisätty kuvia, mutta tuotteen esikatselukuvaa ei voida vielä näyttää. Saadaksesi esikatselukuvan näkyviin, siirry tuotteen sivulle, tallenna sivu ja palaa takaisin hakunäkymään.',
  MODEL_CODE: 'mallinumero',
  MODEL_CODES: 'mallinumerot',
  moveAwayFromPage: 'Siirry pois sivulta',
  MOVE_TO_ORDER_STATUS_PAGE: 'Siirry tilauksen status -sivulle',
  MULTI_PRODUCT_TYPE: 'Mikä tahansa',
  N_ASSORTMENTS: 'valikoimaa',
  N_CAMPAIGNS: 'kampanjaa',
  N_COLORS: 'väriä',
  N_IMAGES: 'kuvaa',
  N_MAINTENANCES: 'huoltoa',
  N_MESSAGES: 'viestiä',
  N_PRODUCTS: 'tuotetta',
  N_ROWS: 'riviä',
  N_SEARCHES: 'hakua',
  N_ORDERS: 'tilausta',
  NAME: 'Nimi',
  NAVBAR_TITLE_IMAGE_SEARCH: 'Kuvahaku',
  NAVBAR_TITLE_PIM: 'PIM+',
  NAVBAR_TITLE_SALES_ORDERS: 'Tilaukset',
  NAVBAR_TITLE_VALIKOIMAT: 'Valikoimanhallinta',
  NAVBAR_TITLE_BIKE_LEASING: 'Työsuhdepyorat',
  NET_PRICE: 'Nettohinta',
  NEW_COLOR: 'Uusi väri',
  NEW_COLUMN_PRESET: 'Uusi sarakemalli',
  NEW_DEFAULT_COMPONENT_HELP_TEXT:
    'Lisää uusi oletuslajitelma täydentämällä riveittäin haluamasi koko ja kappalemäärä. Voit käyttää mitä tahansa kokoja ja määriä, ja oletuslajitelma tallennetaan antamillasi tiedoilla tietokantaan. Huomioithan oletuslajitelmaa lisätessäsi, että koot tulee järjestää taulukossa pienimmästä suurimpaan esim. 1) XS 2) S 3) M...',
  NEW_MESSAGE: 'Uusi viesti',
  NEW_SEARCH: 'Uusi haku',
  NEW: 'Uusi',
  NEW_NOS_PRODUCT: 'Uusi',
  NEW_NOS_PICKED: 'Oma poiminta',
  NOS_PRODUCT_SEARCH: 'NOS-tuotehaku',
  newProduct: 'Uutuus',
  newUntil: 'Näytä uutena (verkkokauppa)',
  NEXT_DELIVERY_DATE: 'Seuraava toimituspäivä',
  NEXT_PAGE: 'Seuraava sivu',
  NO_BALANCE: 'Ei saldoa',
  NO_CANCEL: 'Ei, peruuta',
  NO_CHAR_LIMIT: 'Ei merkkirajoitusta',
  NO_COMPONENTS: 'Ei lajitelmia',
  NO_IMAGE: 'Kuva ei olemassa',
  NO_MESSAGES_LAST_WEEK: 'Ei uusia viestejä viime viikon aikana',
  NO_NAME_SEARCH: 'Nimetön haku 1',
  NO_ORDERS: 'Ei tilauksia',
  NO_RESULTS_SHORT: 'Ei tuloksia',
  NO_RESULTS: 'Antamillasi hakuehdoilla ei löytynyt yhtään tuotetta.',
  NO_RESTRICTIONS: 'Ei rajoituksia',
  NO_SELECTION: 'Ei valintaa',
  NO: 'Ei',
  NOS_ASSORTMENT_LINKS: 'NOS-valikoimakiinnitykset',
  NOS_ORDER: 'NOS-tilaus',
  NOS_ORDERS: 'NOS-tilaukset',
  NOS_ORDER_PROPOSAL_SUMMARY: 'Tilausehdotuksen sisältö ja kokonaissumma',
  NOS_PRODUCT: 'NOS-tuote',
  NOS_PRODUCT_NOT_FOUND: 'Tuotetta ei löytynyt',
  NOS_PRODUCTS: 'NOS-tuotteet',
  NOS_PRODUCT_EDIT_SEARCH_DIALOG_TITLE: 'Muokkaa hakuehtoja',
  NOS_MANY_PRODUCTS: 'NOS-tuotetta',
  NOS_SEARCH_ORDERS_USING_EANS: 'Etsi tuotetta tilausehdotuksilta tuotteen EAN-koodilla',
  NOS_SELECTION: 'NOS-valikoima',
  NOS_ORDER_SUGGESTIONS_ENABLED: 'NOS-tilausehdotukset sallittu',
  NOS_ORDERS_HISTORY: 'NOS-tilausten historia',
  NOS_ORDER_SENT_AUTOMATICALLY:
    'Tilausehdotus lähetetään automaattisesti eteenpäin määräajan umpeutuessa. Tilausmäärien muuttaminen päättyy ',
  NOS_ORDER_SUMS_CHANGED:
    'Kokonaissummat ja EAN-kappaleiden yhteenlaskettu määrä tilauksella ovat mahdollisesti muuttuneet. Haluatko päivittää tiedot?',
  NOS_NO_OPEN_ORDER_PROPOSAL: 'Ei avointa tilausehdotusta',
  NOS_NO_OPEN_ORDER_PROPOSAL_DESCRIPTION_1:
    'Tilausehdotus päivitetään sivulle joka päivä klo 9.15 mennessä, ',
  NOS_NO_OPEN_ORDER_PROPOSAL_DESCRIPTION_2:
    'jonka jälkeen sitä voi muokata klo 13.00 asti. Tilausehdotusta ei näytetä, jos täydennettävää ei ole, eli varastosaldot ovat kunnossa.',
  NOS_VARIANT_RULE: 'Täydennysehdotuksen tilaussääntö',
  NOS_VARIANT_RULES_DIALOG_TITLE:
    'Määritä täydennysehdotusten tilaussäännöt varianteille nykyiselle ja tuleville sesongeille',
  NOS_VARIANTS_INCLUDED_COUNT: 'Sallittu (kpl)',
  NOS_VARIANTS_BLOCKED_COUNT: 'Estetty (kpl)',
  NOS_RULE_DEFAULT_TOOLTIP:
    'Saat täydennysehdotuksia silloin, kun variantti kuuluu ketjun suositukseen kauppasi kokoluokassa.',
  NOS_RULE_DEFAULT_LABEL: 'Kauppakoon mukainen oletus',

  NOS_RULE_BLOCK_TOOLTIP:
    'Et saa tästä variantista automaattisia täydennysehdotuksia, vaikka ketju suosittelisi sitä kauppakoollesi.',
  NOS_RULE_BLOCK_LABEL: 'Estä ehdotukset',
  NOS_RULE_INCLUDE_TOOLTIP:
    'Saat täydennysehdotuksia tästä variantista aina, vaikka se kuuluisi toisen kauppakoon valikoimaan tai avoimeen valikoimaan.',
  NOS_RULE_INCLUDE_LABEL: 'Salli ehdotukset',
  NOS_RULE_ALL_BLOCKED: 'Estettyjä variantteja',
  NOS_RULE_ALL_INCLUDED: 'Sallittuja variantteja',
  NOS_RULE_PARTIAL: 'Useita sääntöjä',
  NOS_RULE_STATUS: 'Tilaussääntö',
  NOS_RULES_MASS_EDIT_HEADING: 'Täydennysehdotusten massapäivitys',
  NOS_RULES_MASS_EDIT_HELP_TEXT:
    'Päivitä valittujen nimikkeiden kaikkien varianttien NOS-tilaussäännöt. Tämä valinta ylikirjoittaa kaikki aiemmat asetukset',
  NOS_RULES_MASS_EDIT_DEFAULT_CONFIRMATION_HEADER:
    'Vahvista siirtyminen automaattiseen suositukseen',
  NOS_RULES_MASS_EDIT_DEFAULT_CONFIRMATION_TEXT:
    'Tämä palauttaa automaattiset täydennysehdotukset valittujen nimikkeiden nykyisille väreille ja varianteille kauppasi kokoluokan mukaiseksi. Valinta ylikirjoittaa aiemmat asetukset kuluvalle ja tuleville sesongeille.',
  NOS_RULES_MASS_EDIT_INCLUDE_CONFIRMATION_HEADER: 'Vahvista täydennysehdotusten salliminen',
  NOS_RULES_MASS_EDIT_INCLUDE_CONFIRMATION_TEXT:
    'Tämä sallii automaattiset täydennysehdotukset valittujen nimikkeiden nykyisille väreille ja varianteille vaikka ne kuuluisivat jonkin toisen kokoluokan valikoimaan. Valinta ylikirjoittaa aiemmat asetukset kuluvalle ja tuleville sesongeille.',
  NOS_RULES_MASS_EDIT_BLOCK_CONFIRMATION_HEADER: 'Vahvista täydennysehdotusten estäminen',
  NOS_RULES_MASS_EDIT_BLOCK_CONFIRMATION_TEXT:
    'Tämä estää automaattiset täydennysehdotukset valittujen nimikkeiden nykyisiltä väreiltä ja varianteilta. Valinta ylikirjoittaa aiemmat asetukset kuluvalle ja tuleville sesongeille.',
  NOS_MASS_UPDATE_RULES_ERROR: 'Täydennysehdotusten massapäivitys epäonnistui',
  NOS_UPDATE_VARIANT_RULES_ERROR: 'Täydennysehdotusten päivitys epäonnistui',
  NOS_NEW_STATUS: 'Uutuudet',
  NOT_ATTACHED_TO_CAMPAIGN: 'Kiinnittämättömät',
  NOT_CHOSEN: 'Ei valittu',
  NOT_COMPLETED: 'Ei täydennetty',
  NOT_EDITED: 'Ei muokattu',
  NOT_ORDERED: 'Ei tilattu',
  NOT_KNOWN: 'Ei tiedossa',
  NO_ORDER_ROWS: 'Ei tilausrivejä',
  NO_CAMPAIGNS_FOUND: 'Kampanjoita ei löytynyt',
  NOT_SENT: 'Ei lähetetty',
  NOTICE: 'Huomio',
  NO_CAMPAIGN_COLOR_ROLE: 'Ei ohjausmerkkiä',
  NUMBER_OF_COPIES: 'Kappalemäärä',
  NUMBER_OF_RESULT_PRODUCTS: 'Tuotteita hakuehdoilla:',
  OFFER_NET_PRICE: 'Tarjousnetto',
  OK: 'Selvä',
  ON_HOLD_MAINTENANCES: 'Hoitamattomat varaukset',
  ON_NEXT_LOADING_DATE: 'Seuraavana kuormapäivänä',
  ONE_CAMPAIGN: 'kampanja',
  ONE_COLOR: 'väri',
  ONE_IMAGE_TABE_HEADER: 'Kuva',
  ONE_IMAGE: 'kuva',
  ONE_MAINTENANCE: 'Huolto',
  ONE_MESSAGE: 'viesti',
  ONE_PRODUCT: 'tuote',
  ONE_PRODUCTS: 'yhtä tuotetta',
  ONE_ROW: 'rivi',
  ONE_SEARCH: 'haku',
  ONE_ORDER: 'tilaus',
  ONLY_IN_WEB_LABEL: 'Tuotteella näytetään "Vain verkossa" -merkintä',
  ONLY_PRODUCTS_WITH_FULL_DATA: 'Vain rikastetuilla tuotteilla',
  OLD: 'Vanha',
  onlyInStore: 'Myymälätuote (Vain C&C)',
  onlyinweb: 'Vain verkossa tuote',
  onlyinwebUntil: 'Vain verkossa tuote (pvm)',
  OPEN_AS_TABS: 'Avaa välilehtiin',
  OPEN_LOCKED: 'Avaa lukitus',
  OPEN_PRODUCTS: 'Avaa',
  OPEN_SIDEBAR: 'Avaa sidebar',
  OPEN: 'Avaa',
  OPENING_HOUR_EXCEPTIONS: 'Poikkeusaukioloajat',
  OPENING_HOURS: 'Aukioloajat',
  OPTIONAL: 'vapaaehtoinen',
  OR: 'tai',
  ORDER_ACCEPT_DATE: 'Tilauksen hyväksymispäivä',
  ORDER_ACCEPT_DATE_WITHIN_WEEK: 'Viikon sisällä',
  ORDER_ACCEPT_DATE_WITHIN_MONTH: 'Kuukauden sisällä',
  ORDER_ACCEPT_DATE_WITHIN_3_MONTHS: '3 kk sisällä',
  ORDER_ACCEPT_DATE_OLDER_THAN_3_MONTHS: 'Yli 3 kk vanhat',
  ORDER_ACCEPT_DATE_OLDER_THAN_6_MONTHS: 'Yli 6 kk vanhat',
  ORDER_CONTENT_AND_ACCEPTED_STATUS: 'Tilauksen sisältö ja hyväksyminen',
  ORDER_DATE: 'Tilauspäivä',
  ORDER_INFO: 'Tilauksen tiedot',
  ORDER_NAME: 'Tilauksen nimi',
  ORDER_NOT_FOUND: 'Tilausta ei löytynyt',
  ORDER_NUMB: 'Tilausnro',
  ORDER_NUMBER: 'Tilausnumero',
  ORDER_PAGE: 'Tilauksen sivu',
  ORDER_PROPOSAL: 'Tilausehdotus',
  ORDER_PROPOSAL_INFO: 'Tilausehdotuksen tiedot',
  ORDER_PROPSAL_STATUS: 'Tilausehdotuksen status',
  ORDER_PROPOSAL_ARRIVED: 'Tilausehdotus saapunut',
  ORDER_SAP_ID: 'Tilauksen SAP-numero',
  ORDER_SAP_IDS: 'Tilauksen SAP-id:t',
  ORDER_SENT_IN_BACKGROUND:
    'Tilaus lähetetään nyt SAP:iin ja B24:ään taustalla. Tilauksen status päivittyy automaattisesti, kun tilaus on käsitelty.',
  ORDER_MANAGEMENT: 'Tilaustenhallinta',
  ORDER_ROWS_SENDING:
    'Tilausmäärien lähetys on käynnissä. Odota vielä hetki ennen sivulta poistumista tai tilauksen hyväksymistä.',
  ORDER_STATUS: 'Tilauksen status',
  ORDER_TYPE: 'Tilauksen tyyppi',
  ZBAN: 'Suoratoimitus',
  ZNOR: 'Keskusvarastotoimitus',
  SEARCH_CAMPAIGNS: 'Etsi kampanjaa',
  ADD_NEW_CAMPAIGN: 'Lisää uusi kampanja',
  ADD_AND_REORDER_COLOR_MEDIA: 'Lisää ja järjestä kuvia tai videoita värille',
  CAMPAIGN_DURATION: 'Kampanjapäiviä yht.',
  CAMPAIGN_TYPE: 'Kampanjatyyppi',
  MARKETING_BEFORE_START: 'Ennakkomarkkinoinnissa',
  MARKETING_STARTS: 'Ennakkomarkkinointi alkaa',
  CREATE_CAMPAIGN: 'Luo kampanja',
  CHOOSE_DATE_DAY: 'Valitse päivä',
  PRE_MARKETING_STARTS: 'Koska ennakkomarkkinointi alkaa',
  CAMPAIGN_HAS_PRE_MARKETING: 'Valitse onko kampanja ennakkomarkkinoinnissa',
  CHAIN_CAMPAIGN: 'Ketjukampanja',
  WEB_CAMPAIGN: 'Verkkokauppakampanja',
  CHOOSE_CAMPAIGN_TYPE: 'Valitse onko kampanja',
  CHOOSE_CAMPAIGN_DURATION: 'Valitse kampanjan kesto',
  GIVE_CAMPAIGN_A_NAME: 'Anna kampanjan nimi',
  CREATE_NEW_CAMPAIGN: 'Luo uusi kampanja',
  CAMPAIGN_MAX_PRODUCTS: 'Kampanjatuotteiden rajoitus',
  CAMPAIGN_PRODUCTS_MAX_LIMIT: 'Kampanjatuotteiden maksimimäärä',
  PRODUCTS_ADD_TO_CAMPAIGN_ERROR: 'Tuotteiden lisääminen kampanjaan epäonnistui',
  PRODUCTS_ADD_TO_CAMPAIGN_ERROR_MAX_PRODUCTS:
    'Tuotteiden lisääminen kampanjaan epäonnistui, koska kampanjatuotteiden maksimimäärä on ylitetty.',
  PRODUCTS_ADD_TO_CAMPAIGN_ERROR_MAX_PRODUCTS_SOME:
    'Joidenkin tuotteiden lisääminen kampanjaan epäonnistui, koska kampanjatuotteiden maksimimäärä on ylitetty.',
  ADD_TO_CAMPAIGN: 'Lisää kampanjaan',
  CAMPAIGN_DATES: 'Kampanjan ajankohta',
  CHANGE_CAMPAIGN_NAME: 'Vaihda kampanjan nimi',
  EDIT_PRODUCT_PRICE: 'Hinnoittele tuote',
  PRODUCTS_ADDED_TO_CAMPAIGN: 'Tuotteet lisätty kampanjaan',
  PRODUCTS_WERE_UPDATED_SUCCESSFULLY: ' tuotetta päivitetty onnistuneesti',
  PRODUCT_WAS_UPDATED_SUCCESSFULLY: 'Tuote päivitetty onnistuneesti',
  PRODUCT_MASS_EDIT_FAILED: 'Tuotteiden massamuokkaus epäonnistui',
  PRODUCT: 'tuote',
  PRODUCTSS: 'tuotetta',
  EDIT_CAMPAIGN_PRODUCT_COLORS: 'Muokkaa tuotteen värejä',
  ORDER_HISTORY: 'Tilaushistoria',
  ORDER_SUM_VAT: 'Kokonaissumma alv.',
  ORDER_SYMBOLS: 'Tuoterooli',
  ORDER_UNIT_AMOUNT: 'Perusyksiköiden määrä',
  ORDER_UNIT: 'Ostomääräyksikkö',
  ORDER_VALUE: 'Tilauksen arvo',
  ORDER_AMOUNT: 'Tilausmäärä',
  ORDERED_AMOUNT: 'Tilausmäärä',
  ORDERED_ITEM_AMOUNT: 'Tilattu määrä',
  SEND_ALL_ORDERS: 'Lähetä kaikki tilaukset',
  ORDERS_SENDING: 'Tilausten lähetys käynnissä',
  ORDERS_ERRORS: 'Tilauksia virheessä',
  ORDERS_CANCELLED: 'Tilauksia peruttu',
  ORDERS_CHANGES: 'Muutoksia tilauksissa',
  ORDERS_NOT_SENT: 'Tilauksia ei lähetetty',
  EDIT_PRICES: 'Muokkaa hintoja',
  SET_DELIVERY_DATES: 'Aseta toimitusajat',
  SET_DELIVERY_DATE: 'Aseta toimitusaika',
  SET_PRICING: 'Hinnoittele',
  NAVBAR_TITLE_CAMPAIGNS: 'Kampanjatyökalu',
  CAMPAIGN_START_DATE_UPDATE_FORBIDDEN: 'Kampanjan alkupäivää ei voi muuttaa',
  CAMPAIGN_END_DATE_UPDATE_FORBIDDEN: 'Kampanjan loppupäivää ei voi muuttaa',
  CAMPAIGN_ENDED: 'Kampanjan tietoja ei voi muuttaa, sillä kampanja on päättynyt',
  ARCHIVED: 'Arkistoitu',
  PARTIALLY_SENT: 'Osittain lähetetty',
  CANCELLATIONS: 'Peruutuksia',
  ORDERS_WARNINGS: 'Hyväksymättömiä tilauksia',
  SEND_ORDER_AGAIN: 'Lähetä uudelleen',
  orderMaxAmount: 'Max tilaus määrä',
  ORDERS_APPROVAL_MISSING: 'Tilaukset joista puuttuu hyväksyntä',
  ORDERS_FILTER_RESULT: 'Hakuehtoja vastaavia tilauksia',
  OVERRIDE_SEARCH_PREFIX: 'Hakuehtojen muokkaukset tallentuvat automattisesti hakuun',
  OVERRIDE_SEARCH_SUFFIX: 'Oletko varma, että haluat jatkaa?',
  OVH_31_DAYS_IN: 'Tuote on ollut 31pv ovh:lla seuraavaksi',
  OVH_DAYS_BEHIND: 'OVH-PÄIVIÄ TAKANA',
  OVH_DAYS_LEFT: 'OVH-päiviä jäljellä',
  OVH_DAYS: 'OVH-PÄIVIÄ',
  OVH_PRICE: 'ovh.',
  OVH_PRICING: 'ovh. hinnoittelu',
  OVH: 'Normaalihinta',
  ovhPriceEnds: 'Jäädytä OVH hinta päivään',
  OWN_BALANCE: 'Oma saldo',
  packagePrice: 'Paketti hinta',
  PAGE_NOT_FOUND: 'Oho! Etsimääsi sivua ei löytynyt.',
  USER_NOT_ALLOWED: 'Oho! Pääsy kielletty.',
  USER_NO_ACTIVE_SERVICE_FOUND: 'Käyttäjältä ei löytynyt yhtään aktiivista palvelua.',
  USE_NOS_DEFAULT_SETTINGS: 'Käytä oletusasetuksia',
  USE_NOS_DEFAULT_SETTINGS_DESC:
    'Palauttaa variantit ketjun oletusvalikoiman mukaiseen täydennykseen.',
  USE_NOS_INCLUDE: 'Salli NOS-tilauksilla',
  USE_NOS_INCLUDE_DESC:
    'Luo täydennysehdotuksia kaikista varianteista riippumatta valikoiman kokoluokasta.',
  USE_NOS_BLOCK: 'Estä NOS-tilauksilta',
  USE_NOS_BLOCK_DESC:
    'Pysäyttää automaattiset täydennysehdotukset kaikilta nimikkeen varianteilta.',
  PAST_MAINTENANCES: 'Menneet varaukset',
  PAC: 'pkt',
  PART_ORDER_STATUS_SENT: 'Lähetetty',
  PER_PAGE: 'Näytä sivulla',
  permissionError: 'Käyttöoikeusvirhe',
  permissionErrorDescription:
    'Kirjaudu uudelleen sisään. Jos ongelma ei poistu, ota yhteyttä ylläpitoon.',
  pagePendingChanges: 'Sivulla on tallentamattomia muutoksia',
  pagePendingChangesDescriptionError:
    'Sivulla on virheellisiä tai puutteellisia tietoja, jotka estävät tallennuksen.',
  pagePendingChangesDescription:
    'Sivulla on tallentamattomia muutoksia. Haluatko varmasti poistua sivulta?',
  pagePendingChangesComplete: 'Muutokset ovat nyt tallennettu ja voit poistua sivulta.',
  PHONE: 'Puhelinnumero',
  PHOTO_COLOR: 'Kuvausväri',
  PHOTO_COLORS: 'Kuvausvärit',
  pid: 'Pid',
  PIM: 'PIM',
  PLANT: 'Jakelukeskus',
  PREORDER: 'Ennakkotilaus',
  PREORDER_DISABLED:
    'Ennakkotilaamisen määrittäminen ei ole käytettävissä. Jotta voit asettaa tuotteen ennakkotilaustuotteeksi, estä sen myynti seurakaupassa.',
  PREORDER_INFO_TEXT_1:
    'Huom! Jotta tuote voidaan näyttää verkkokaupassa ennakkotilaustuotteena, pitää sillä olla myös verkon varastossa saldoa SAP:in päässä.',
  PREORDER_INFO_TEXT_2:
    'Kun tuote tulee myöhemmin verkkokaupan varastoon (Stage), ennakkotilaamiseen liittyvät tiedot piilotetaan automaattisesti verkkokaupan tuotesivulta, koska tuote on silloin normaaliin tapaan asiakkaan ostettavissa.',
  PREORDER_INFO: 'Näkyvyys ennakkotilaustuotteena',
  PREORDER_PRODUCT: 'Näytä tuote verkkokaupassa ennakkotilaustuotteena',

  PREORDER_WEEK: 'Ennakkotilauksen toimitusviikko',
  preorderInfo: 'Ennakkotilaus info',
  PREVIOUS_PAGE: 'Edellinen sivu',
  PRICE_CALCULATION: 'Alennuksen laskentamalli',
  PRICE_COMPARISON_TITLE: 'Hintaseuranta',
  PRICE_COMPARISONS: 'kilpailijaa',
  PRICE_DEDUCTION: 'ovh. hinnasta',
  PRICE_MARGIN: 'Kate',
  PRICE_NET: 'Netto',
  PRICE_REFILL: 'Täydennyshinta',
  PRICE_PURCHASE: 'Tarjousnetto',
  PRICE_RETAIL_SHORT: 'ISH',
  PRICE_RETAIL: 'Normaalihinta',
  PRICE_SPECIAL: 'Erikoistarjoushinta',
  PRICE_WITH_VAT: 'alvillinen hinta',
  price: 'Hinta',
  PRICING_ADDED: 'Hinnoittelu lisätty',
  PRICING_AVERAGE: 'Alennushintojen keskiarvo',
  PRICING_MEDIAN: 'Alennushintojen mediaani',
  PRICING_TYPE_LAST_CHANCE: 'Poistomyyntihinnoittelu',
  PRICING_TYPE_SAP_CAMPAIGN: 'Kampanja-alennus (SAP)',
  PRICING_TYPE_SHOP: 'Tuotekohtainen alennushinta (myymälät)',
  PRICING_TYPE_WEB: 'Tuotekohtainen alennushinta (verkkokauppa)',
  PRICING_TYPE: 'Hintatyyppi',
  PRICING_VISIBILITY_SHOP: 'Myymälät',
  PRICING_VISIBILITY_WEB: 'Verkkokauppa',
  PRIMARY_DELIVERY_WEEK_SHORT: 'Ensisij. toim. viikko',
  printDescription: 'Lyhyt tuotekuvaus',
  PRODUCT_ADD_ERROR_TITLE: 'Valittua tuotetta ei voitu lisätä valikoimaan',
  PRODUCT_ACCEPTED_MARKETING: 'Tuotekortti hyväksytty markkinointiin',
  PRODUCT_ACCEPTED: 'Tuotekortti hyväksytty',
  PRODUCT_ACTIVATION_TIME_DISABLED:
    'Aktivoinnin ajastaminen ei ole käytettävissä. Jotta voit ajastaa tuotteen, estä sen myynti seurakaupassa.',
  PRODUCT_ACTIVE_FROM_TIME: 'Tuotteen aktivointi sallittu aikaisintaan',
  PRODUCT_AMBIANCE_IMAGES_DESCRIPTION:
    'Fiiliskuvat ovat värikohtaisia insipiraatiokuvia, jossa tuote on kuvattuna mallin päällä ja oikeassa käyttökontekstissaan kuten ulkona tai soveltuvan lajin parissa.',
  PRODUCT_AMBIANCE_IMAGES: 'Fiiliskuvat',
  PRODUCT_CODE: 'Tuotekoodi',
  PRODUCT_COLOR_SEASONS: 'Kiinnitetyt sesongit',
  PRODUCT_CREATED_IN_SAP_AMOUNT: 'Tuotteet avattu SAP:iin',
  PRODUCT_DATA_STATUS: 'Tuotetietojen tilanne',
  PRODUCT_DATA_VALID: 'Tuotedata kunnossa',
  PRODUCT_DESCRIPTION: 'Tuotekuvaus',
  PRODUCT_DROP_SHIPPING: 'Toimitusmyynti',
  PRODUCT_EDIT_SIZES: 'Muokkaa tuotteen EAN- ja kokotietoja',
  PRODUCT_EDIT_SIZES_SUCCESS: 'Tuotteen EAN- ja kokotietojen muutokset tallennettu.',
  PRODUCT_ENABLE_SELLING_TIME: 'Tuotteen myynti sallittu aikaisintaan',
  PRODUCT_FAMILIY: 'Tuoteperhe',
  PRODUCT_GROUP: 'Tuoteryhmä',
  PRODUCT_HAS_SALES_UNIT: 'Tuotteeseen liittyy myyntierä (vapaaehtoinen)',
  PRODUCT_HISTORY: 'Tuotehistoria',
  PRODUCT_IMAGE_MISSING: 'Tuotteella ei ole kuvaa.',
  PRODUCT_IMAGE: 'Tuotteen kuva',
  PRODUCT_IMAGES_DESCRIPTION:
    'Packshot-kuvat ovat kuvia, joissa tuote esiintyy yksinään ilman mallia.',
  PRODUCT_IMAGES_MAIN: 'Ensisij. kuva',
  PRODUCT_IMAGES: 'Tuotekuvat',
  PRODUCT_IN_SAP: 'Tuote SAP:issa',
  PRODUCT_INFO_COMPLETE: 'Taustatiedot täydennetty',
  PRODUCT_INFO_COPIED: 'Tuotteen perustiedot kopioitu',
  PRODUCT_INFO: 'Tuotteen tiedot',
  PRODUCT_LABELS: 'Kestävä kehitys',
  PRODUCT_LEASING: 'Salli pyörän myynti verkossa työsuhdeleasingpyöränä',
  PRODUCT_MAIN_COLOR: 'Näytä verkkosivun tuotenostossa kuva SAP-väriltä',
  PRODUCT_MANAGEMENT: 'Tuotetiedonhallinta',
  PRODUCT_MARKETING_STATUS: 'Markkinoinnin tilanne',
  PRODUCT_NAME_AND_CATEGORIES: 'Tuotteen nimi ja tavararyhmittely',
  PRODUCT_NAME: 'Tuotteen nimi',
  PRODUCT_OR_COLOR_CODE: 'Valmistajan tuotekoodi tai pricat ID',
  PRODUCT_POSSESIVE: 'tuotteen',
  PRODUCT_POTENTIAL: 'Lajipotentiaali',
  PRODUCT_PRICE: 'Tuotteen hinta €',
  PRODUCT_QUICK_SEARCH_DESCRIPTION:
    'Hae yksittäistä tuotetta SAP-, KW- tai EAN-numerolla ja siirry suoraan tuotteen sivulle. \n\n Tuotteet-sivulla aktiivisena olevaan hakuusi ei tehdä muutoksia pikahaun yhteydessä, mutta pikahaussa antamasi tuote avataan normaaliin tapaan välilehdeksi.',
  PRODUCT_QUICK_SEARCH_ERROR: 'Antamallasi hakuehdolla ei löytynyt tuotetta',
  PRODUCT_QUICK_SEARCH_GO: 'Siirry tuotteen sivulle',
  PRODUCT_QUICK_SEARCH_HEADING: 'Tuotteen pikahaku',
  PRODUCT_QUICK_SEARCH_INPUT_LABEL: 'SAP-, KW- tai EAN-numero',
  PRODUCT_SAP_ID: 'Tuotteen SAP-id',
  PRODUCT_SAP_NAME: 'SAP-tuotenimi',
  PRODUCT_SAP_COLOR_NAME: 'SAP-värinimi',
  PRODUCT_SELLING_TIME_DISABLED:
    'Myynnin ajastaminen ei ole käytettävissä. Jotta voit ajastaa myynnin, estä sen myynti seurakaupassa.',
  PRODUCT_SIZES_TITLE: '-tuotteen EAN- ja kokotiedot',
  PRODUCT_STUDIO_IMAGES_DESCRIPTION:
    'Studiokuvat ovat kuvia, joissa tuote on kuvattuna mallin yllä ja kuvan tausta on tyypillisesti yksivärinen.',
  PRODUCT_STUDIO_IMAGES: 'Studiokuvat',
  PRODUCT_TYPE: 'Tuotetyyppi',
  PRODUCT_WARRANTY_TEXT: 'Takuutieto',
  PRODUCT_ROLE: 'Tuoterooli',
  productCampaignListLabel: 'Tuotekampanjan tuotelista "label"',
  productgroup: 'Tuoteryhmät',
  productname: 'Tuotenimi',
  PROD_ADD_TO_ASS_FAILED: 'Tuotteiden lisääminen valikoimaan epäonnistui. ',
  PROD_ADD_TO_ASS_BRAND_MISSING: 'Puutteelliset bränditiedot',
  PROD_ADD_TO_ASS_DUPLICATE_COLORS: 'Duplikaatit SAP värit',
  PROD_ADD_TO_ASS_NO_PRODUCTS: 'Ei lisättäviä tuotteita',
  PROD_ADD_TO_ASS_NO_PRODUCT_CODES: 'Puutteellinen mallinumero',
  PROD_ADD_TO_ASS_ALREADY_IN_ASS: 'Tuote löytyy jo valikoimasta',
  PROD_ADD_TO_ASS_SAP_PRODUCT_ERROR: 'Virhe SAP-haussa',
  PROD_ADD_TO_ASS_KW_ALREADY_EXISTS:
    'KW-numero on jo käytössä toisella tuotteella tai tuote on lisättynä toiseen valikoimaan, mutta sillä ei ole SAP-id:tä.',
  PROD_ADD_TO_ASS_MULTIPLE_PRODUCTS: 'Tuotteen värit linkittyvät useammille päätuotteille',
  PROD_ADD_TO_ASS_KW_MISSING: 'Tuotekoodi puuttuu',
  PROD_ADD_TO_ASS_INVALID_KW: 'Olemassaoleva tuotekoodi ei vastaa uutta tuotekoodia.',
  PROD_ADD_TO_ASS_SAP_SINGULAR_ITEM: 'SAP yksittäisnimike',
  PROD_ADD_TO_ASS_INVALID_SHIP_DATES: 'Virheelliset toimituspäivämäärät',
  PROD_ADD_TO_ASS_INVALID_VARIATION_SAP_IDS: 'Duplikaatit variaation SAP id:t',
  PROD_ADD_TO_ASS_INVALID_COLOR_SWITCH: 'Valmistajan värikoodin vaihto epäonnistui',
  PROD_ADD_TO_ASS_INVALID_COLOR_SWITCH_MISSING_DATA:
    'Valmistajan värikoodin vaihto epäonnistui, väritieto puuttuu.',
  PROD_ADD_TO_ASS_INVALID_COLOR_SWITCH_DUPLICATE_VARIANT:
    'Valmistajan värikoodin vaihto epäonnistui; duplikaatteja kokoja',
  PROD_ADD_TO_ASS_BRAND_CODE_MISSING: 'Brändiltä puuttuu tuotekoodi',
  PROD_ADD_TO_ASS_DUPLICATE_EANS: 'Duplikaatit EAN-koodit Pricatilla',
  PROD_ADD_TO_ASS_DUPLICATE_VARIANTS: 'Duplikaatit tuotteen variantit Pricatilla',
  PROD_ADD_TO_ASS_DUPLICATE_EXISTING_VARIANTS: 'Duplikaatit olemassa olevat variantit',
  PROD_ADD_TO_ASS_DUPLICATE_VARIANT_COLORS: 'Duplikaatit tuotteen värit',
  PROD_ADD_TO_ASS_INVALID_TEKSO_GROUP: 'Virheellinen / puuttuva Tekso-tavararyhmä',
  PROD_ADD_TO_ASS_INVALID_FEDAS_CODE:
    'Pricatin Fedas-koodia ei löydy järjestelmästä tai se puuttuu',
  PROD_ADD_TO_ASS_INVALID_VARIANTS: 'Virheelliset tuotteen variantit',
  PROD_ADD_TO_ASS_MISSING_VENDOR: 'Puutteelliset tuotevalmistajan tiedot',
  PROD_ADD_TO_ASS_NO_VARIANTS: 'Tuotteella ei ole variantteja',
  PROD_ADD_TO_ASS_VENDOR_MISSING: 'Toimittajan tiedot puuttuvat',
  PRODUCTS_ADDED_TO_ASSORTMENT: 'Tuotteet lisätty valikoimaan',
  PRODUCTS_AMOUNT_TOTAL_LONG: 'Tuotteet yhteensä',
  PRODUCTS_AMOUNT_TOTAL_SHORT: 'Tuotteet yht.',
  PRODUCTS_DELIVERY_METHOD: 'Tuotteiden toimitustapa',
  PRODUCTS_LIST_BREADCRUMBS: 'Tuotteet',
  PRODUCTS_LIST_LIMIT_INFO:
    'Hakutulosten näyttäminen on rajoitettu 10 000 tulokseen. Hakua vastaavia tuloksia saattaa olla todellisuudessa enemmän kuin mitä hakutuloslistauksessa esitetään.',
  PRODUCTS_SELECTED: 'tuotetta valittu',
  PRODUCTS_TOTAL_SHORT: 'Tuotteita yht.',
  productType: 'Tuotetyyppi',
  PUBLISH_MESSAGE: 'Julkaise viesti',
  PUBLISH: 'Julkaise',
  UNPUBLISH_SALES_ORDER: 'Peruuta julkaisu',
  published: 'Julkaistu',
  PUBLISH_SALES_ORDER: 'Tilauksen julkaisu',
  PUBLISH_SALES_ORDER_CONFIRMATION:
    'Oletko varma, että haluat julkaista tilauksen? Toimitusaikoja ei voi enää muuttaa jälkikäteen.',
  PUBLISH_SALES_ORDER_CONFIRMATION_CENTRAL: 'Oletko varma, että haluat julkaista tilauksen?',
  UNPUBLISH_SALES_ORDER_CONFIRMATION: 'Oletko varma, että haluat peruuttaa tilauksen julkaisun?',
  PURCHASE_VAT_DEFAULT_SHORT: 'Tarjnetto sis. alv.',
  quarter: 'Kvartaali',
  QUICK_SEARCH_DEFAULT_LABEL: 'Pikahaku',
  READY: 'Valmista!',
  RE_LOGIN: 'Kirjaudu uudelleen',
  RECEIPT_TEXT_NAME: 'Kassajärjestelmän kuittitekstiin tulostettava tuotteen nimi',
  RECEIPT_TEXT: 'Kassajärjestelmän kuittiteksti',
  RECOMMENDATION: 'Suositus',
  REFETCH_SAP_IDS: 'Hae SAP id:t uudelleen',
  REFETCH_SUCCEED: 'SAP id haku onnistui',
  REMOVE_ALL_PROPERTIES: 'Poista kaikki ominaisuudet',
  REMOVE_FILTER: 'Poista hakuehto',
  REMOVE_SELECTION: 'Poista valinta',
  REMOVE_WARNING: 'Poista varoitus',
  RENAME_SEARCH: 'Nimeä tallennettu haku uudelleen',
  RENAME: 'Nimeä uudelleen',
  REQUIRED_SAP_DATA_NOT_OK: 'SAP-tiedot vajaat tai virheelliset',
  REQUIRED_SAP_DATA_OK: 'SAP-tiedot OK',
  RESERVATION_CREATED: 'Varaus luotu',
  RESET_DEFAULTS: 'Palauta oletukset',
  RESET_SEARCH_FILTERS: 'Tyhjennä hakuehdot',
  RETURN_AND_FIX: 'Korjaan tiedot',
  RELOAD_PAGE: 'Lataa sivu uudelleen',
  ROLE_TIER1: 'Tier 1',
  ROLE_TIER2: 'Tier 2',
  ROLE_TIER3: 'Tier 3',
  ROLE_MEGA: 'Megat / kärjet',
  ROLE_MUST_WIN_SPRING_SUMMER: 'Must win: kevät/kesä',
  ROLE_MUST_WIN_AUTUMN: 'Must win: syksy',
  ROLE_MUST_WIN_WINTER: 'Must win: talvi',
  ROLE_MUST_WIN_OTHER: 'Must win: muut',
  ROLE_BATCH: 'Erätuotteet',
  ROLE_365_POOL: '365 pooli',
  REJECT: 'Hylkää',
  SALE_BEFORE_START: 'Tuotetta saa myydä ennen alennusta',
  SALE_BY_DISCOUNT_PRICE: 'alehinnalla',
  SALE_BY_OVH_PRICE: 'ovh-hinnalla',
  SALE_CAMPAIGN: 'Kampanja',
  SALE_COLORS: 'Hinnoittelun alaiset värit',
  SALE_DAYS: 'Myyntipäivät',
  SALE_ENDS: 'Hinnoittelu päättyy',
  SALE_LIMITED: 'Myynti rajoitettu',
  SALE_STARTS: 'Hinnoittelu alkaa',
  SALE_VISIBILITY: 'Hinnoittelu käytössä',
  saleDays: 'Alennus päivät',
  salePercentage: 'Alennus prosentti',
  saleProduct: 'Poistotuote',
  SALES_ORDER: 'Myyntitilaus',
  SALES_ORDER_AMOUNT_CHANGED: 'Määrä muuttunut',
  SALES_ORDER_ARCHIVE_CONFIRM:
    'Oletko varma, että haluat arkistoida tilauksen? Tämä tyhjentää tekemäsi tuotevalinnat.',
  SALES_ORDER_BATCH_ORDER: 'Myyntierätilaus',
  SALES_ORDER_BATCH_SIZE: 'Myyntierä',
  SALES_ORDER_B24_STATUS: 'Tilauksen toimitusseuranta',
  SALES_ORDER_B24_STATUS_HEADING: 'Tuotekohtaiset tilaustiedot viikottain',
  SALES_ORDER_SAP_ID: 'Tilauksen SAP id',
  SALES_ORDER_CHANGED: 'Rivillä muutoksia',
  SALES_ORDER_COMPONENT_ORDER: 'Lajitelmatilaus',
  SALES_ORDER_CONFIG_INPUT_ERROR:
    'Täydennä pakolliset kentät, jotta suositukset voidaan tallentaa.',
  SALES_ORDER_CONFIGURATION: 'Tilauksen asetukset',
  SALES_ORDER_CREATION_FAILED: 'Tilauksen luominen epäonnistui',
  SALES_ORDER_CREATION_SUCCESS: 'Tilaus luotu',
  ORDER_DEADLINE: 'Tilausaika päättyy',
  SALES_ORDER_DEADLINE: 'Kaupan tilausten tilausaika päättyy',
  PURCHASE_ORDER: 'Ostotilaus',
  PURCHASE_ORDER_DETAILS: 'Keskusvaraston EDI-tilauksen tiedot',
  PURCHASE_ORDER_ACCEPT_ACTION: 'Hyväksy tilaus',
  PURCHASE_ORDER_ACCEPTED_ACTION: 'Tilaus hyväksytty',
  PURCHASE_ORDER_ACCEPTED: 'Tilaukset hyväksytty lähetettäväksi',
  PURCHASE_ORDER_ACCEPT_CONFIRM: 'Vahvista Keskusvaraston EDI-tilauksen hyväksyminen',
  PURCHASE_ORDER_ACCEPT_DATE: 'Keskusvaraston EDI-tilauksen hyväksymispäivä',
  PURCHASE_ORDER_ACCEPT_CONFIRM_MESSAGE:
    'Oletko varma, että haluat hyväksyä tilauksen ja sallia tilausten lähettämisen?',
  PURCHASE_ORDER_CONTENT_AND_SENDING: 'Keskusvaraston EDI-tilauksen sisältö ja lähetys',
  PURCHASE_ORDER_DELIVERY_METHOD_AND_ACCEPT:
    'Valitse tilauksen lähetystapa ja hyväksy tilauksien lähetys',
  PURCHASE_ORDER_DELIVERY_TIME_SPECIFIC: 'Toimitusaikakohtaisesti (oletus)',
  PURCHASE_ORDER_COLOR_SPECIFIC: 'Tuotevärikohtaisesti',
  PURCHASE_ORDER_DEADLINE: 'EDI-tilausten lähetyspäivä',
  PURCHASE_ORDER_EDITING_AMOUNT: 'Tilausmääriä tallennetaan',
  PURCHASE_ORDER_ORDERING_ENDS: 'EDI-tilausten lähetyspäivä',
  PURCHASE_ORDER_SAVING: 'Asetuksia tallennetaan',
  PURCHASE_ORDER_SHOP: 'Keskusvaraston EDI-tilauksen kauppa',
  PURCHASE_ORDER_STATUS: 'Keskusvaraston EDI-tilauksen status',
  PURCHASE_ORDER_ERROR: 'Täydennä pakolliset kentät, jotta voit hyväksyä tilauksen.',
  PURCHASE_ORDER_NO_AMOUNTS_COPIED:
    'Tilausmääriä ei ole vielä kopioitu ostotilaukselle, määrien muokkaus on estetty.',
  PURCHASE_ORDER_ITEM_AMOUNTS: 'Tuotteiden määrä myyntitilauksella',
  PURCHASE_ORDER_PART_ORDERS_COUNT: 'Keskusvaraston EDI-tilauksen osatilausten lukumäärä:',
  PURCHASE_ORDER_ORDERED_AMOUNT: 'Tilausmäärä myyntitilauksella',
  PURCHASE_ORDER_TYPE: 'Keskusvaraston EDI-tilauksen tilaustyyppi',
  PURCHASE_TYPE: 'Tilaustyyppi',
  PURCHASE_TYPE_SHOP: 'Yhteisostotilaus',
  PURCHASE_TYPE_CENTRAL: 'Keskusvaraston ostotilaus',
  SALES_ORDER_DEADLINE_ENDED: 'Tilausaika päättyi',
  SALES_ORDER_DELIVERY_DATE_CHANGED: 'Toimituspäivä muuttunut',
  SALES_ORDER_DELIVERYDATES_NOK: 'Puuttuvia toimituspäiviä',
  SALES_ORDER_DELIVERYDATES_OK: 'OK',
  SALES_ORDER_DISABLE_READY_FAILED: 'Tilauksen hyväksynnän poisto epäonnistui',
  SALES_ORDER_DISABLE_READY_SUCCESS: 'Tilauksen hyväksyntä poistettu',
  SALES_ORDER_ENABLED_FOR_SHOPS: 'Tilaus avataan kaupoille',
  SALES_ORDER_INPUT_ERROR:
    'Täydennä pakolliset kentät, jotta tilausmäärät voidaan tallentaa ja tilauksen hyväksymisvalinta vapautuu. Mikäli et halua tilata tuotetta syötä arvoksi 0.',
  SALES_ORDER_LINE_STATUS: 'Tilausrivin status',
  SALES_ORDER_OK: 'Tilaus OK',
  SALES_ORDER_PRODUCT: 'Yhteisostotuote',
  SALES_ORDER_PUBLISH_FAILED: 'Tilauksen julkaiseminen epäonnistui',
  SALES_ORDER_PUBLISH_SUCCESS: 'Tilaus julkaistu',
  SALES_ORDER_PUBLISHED: 'Tilaus julkaistu',
  SALES_ORDER_REPORT_BY_STORE: 'Kauppakohtainen tilausraportti',
  SALES_ORDER_REPORT_BY_ORDER: 'Tilausten koontiraportti',
  SALES_ORDER_REPORT_BY_PURCHASE_ORDER: 'Keskusvarastotilausten koontiraportti',
  SALES_ORDER_REPORT_BY_BRAND: 'Lataa raportti',
  SALES_ORDER_EDI_LIST: 'Lataa EDI tilausnumerot',
  SALES_ORDER_SAP_STATUS: 'Tilausten status',
  SALES_ORDER_SET_ARCHIVED_FAILED: 'Tilauksen arkistointi epäonnistui',
  SALES_ORDER_SET_ARCHIVED_SUCCESS: 'Tilaus arkistoitu',
  SALES_ORDER_SET_READY_FAILED: 'Tilauksen hyväksyminen epäonnistui',
  SALES_ORDER_SET_READY_SUCCESS: 'Tilaus hyväksytty',
  SALES_ORDER_SHOP_AMOUNT: 'Kauppojen määrä tilauksella',
  SALES_ORDER_SHOP_RANK: 'Tilausmäärien oletustaso',
  SALES_ORDER_SHOP_SUMS: 'Tilauksen kauppakohtaiset tilaussummat',
  SALES_ORDER_SHOP: 'Tilaava kauppa',
  SALES_ORDER_STATUS: 'Tilauksen status',
  SALES_ORDER_SUMMARY_ITEMS_AMOUNT: 'Kauppojen yhteenlaskettu EAN-kappaleiden tilausmäärä',
  SALES_ORDER_SUMMARY_NUMBER_OF_SHOPS: 'Tilaukseen osallistuvien kauppojen määrä',
  SALES_ORDER_TOTAL_SUM: 'Tilauksen kokonaissumma',
  SALES_ORDER_UNARCHIVE_FAILED: 'Tilauksen arkistoinnin peruminen epäonnistui',
  SALES_ORDER_UNARCHIVE_SUCCESS: 'Tilauksen arkistointi peruttu',
  SALES_ORDER_VARIANT_ORDER: 'EAN-tilaus',
  SALES_ORDER_VARIANT_TOTAL_AMOUNT: 'EAN-kappaleiden yhteenlaskettu määrä tilauksella',
  SALES_ORDERS_ADMIN: 'Tilausvirheiden hallinta',
  SALES_ORDERS_ACCEPTED: 'Hyväksytyt tilaukset',
  SALES_ORDERS_ACTIVE: 'Aktiiviset tilaukset',
  SALES_ORDERS_ARCHIVED: 'Arkisto',
  SALES_ORDERS_DELIVERY_TRACKING: 'Toimitusseuranta',
  SALES_ORDERS_DELIVERY_TRACKING_LONG: 'Yhteisostotilausten toimitusseuranta',
  SALES_ORDERS_CLOSED: 'Suljetut tilaukset',
  SALES_ORDERS_COPY_AMOUNTS: 'Kopioi suositukset',
  SALES_ORDERS_REPORT: 'Tilausten yhteenveto',
  SALES_ORDERS_SHOP_BASIC_INFO: 'Kaupan perustiedot',
  SALES_ORDERS_SHOP_ENABLED: 'Aktiivinen',
  SALES_ORDERS_SHOP_RANKS: 'Kaupan oletustasot',
  SALES_ORDERS_SHOPS_SUBTITLE:
    'Oletustasot määritellään tuotetyyppikohtaisesti ja ne vaikuttavat tilauksilla kaupalle asetettuihin oletustilausmääriin ja niiden suosituksiin.',
  SALES_ORDERS_SHOPS_TITLE: 'Kauppojen oletustasot',
  SALES_ORDERS_SUMMARY_PAGE_TITLE: 'Tuotekohtaiset ketjun tilausmäärät',
  SALES_ORDERS: 'Tilaukset',
  SALES_UNIT: 'Myyntimääräyksikkö',
  saleStarts: 'Ale alkaa',
  saleEnds: 'Ale loppuu',
  SAP_ACTION_IN_PROGRESS: 'SAP-päivitys käynnissä',
  SAP_CAMPAIGN_ID: 'SAP-kampanjanro',
  SAP_CAMPAIGN_NAME: 'SAP-kampanjan nimi',
  SAP_COLOR_CODE: 'SAP-värikoodi',
  SAP_COLOR: 'SAP-väri',
  SAP_CREATION: 'SAP perustaminen',
  SAP_GROUP_AND_FEDAS: 'SAP-tavararyhmä ja IIC Fedas',
  SAP_ID: 'SAP ID',
  SAP_INFO_ALL_OK: 'Kaikki tarvittavat tiedot on täydennetty',
  SAP_INFO_DIALOG_TITLE_PREFIX: 'Tarkista tuotteen',
  SAP_INFO_DIALOG_TITLE_SUFFIX: '-SAP-integraation taustatiedot',
  SAP_INFO_HIDE: 'Piilota SAP-data',
  SAP_INFO_IN_ORDER: 'kunnossa',
  SAP_INFO_MISSING: 'Puuttuvat tai virheelliset tiedot:',
  SAP_INFO_NOK: 'Korjaa SAP-tiedot',
  DATA_NOK: 'Korjaa tiedot',
  DATA_OK: 'Tiedot OK',
  SAP_INFO_NOT_IN_ORDER: 'vajaat tai virheelliset',
  SAP_INFO_OK: 'SAP-tiedot OK',
  SAP_INFO_REQUIRED: 'Tarvittavat',
  SAP_INFO_SHOW: 'Näytä SAP-data',
  SAP_INFO: 'SAP tiedot',
  SAP_NAME: 'SAP nimi',
  SAP_NET_PRICE: 'Ostohinnoittelun netto',
  SAP_NUMBER: 'SAP-numero',
  SAP_NUMBR: 'SAP-nro',
  SAP_PRODUCT_CREATE_FAIL: 'SAP-perustaminen epäonnistui',
  SAP_PRODUCT_CREATE_SUCCESS: 'SAP-perustaminen onnistui',
  SAP_PRODUCT_MASS_UPDATE_SUCCESS: 'SAP-massapäivitys onnistui',
  SAP_PRODUCT_UPDATE_POST_FAIL: 'SAP-päivitys epäonnistui (POST)',
  SAP_PRODUCT_UPDATE_POST_SUCCESS: 'SAP-päivitys onnistui (POST)',
  SAP_PRODUCT_UPDATE_PUT_FAIL: 'SAP-päivitys epäonnistui (PUT)',
  SAP_PRODUCT_UPDATE_PUT_SUCCESS: 'SAP-päivitys onnistui (PUT)',
  SAP_SIZE: 'SAP-koko',
  SAP_STATUS: 'SAP-status',
  SAP_UPDATE: 'SAP päivitys',
  SAP_UPDATE_IN_PROGRESS: 'SAP-päivitys käynnissä',
  SAP_UPDATED: 'SAP-päivitetty',
  sapId: 'SAP Nimikenumero',
  sapName: 'SAP Tuotenimi',
  sapProductgroup: 'SAP Tuoteryhmä',
  SAVE_AND_CLOSE: 'Tallenna ja sulje',
  SAVE_AND_CLOSE_VIEW: 'Tallenna ja sulje näkymä',
  SAVE_AND_RETURN: 'Tallenna ja siirry hakutuloksiin',
  SAVE_AND_SHOW_SEARCH_RESULTS: 'Tallenna ja näytä hakutulokset',
  SAVE_CHANGES_QUESTION: 'Tallennetaanko muutokset?',
  SAVE_CHANGES: 'Tallenna muutokset',
  SAVE_CHANGES_AND_CLOSE: 'Tallenna muutokset ja sulje',
  SAVE_COLUMNS_AS_PRESET: 'Tallenna sarakemalli',
  SAVE_IMAGE: 'Tallenna kuva',
  SAVE_PRICE: 'Tallenna hinta',
  SAVE_SEARCH: 'Tallenna haku',
  SAVE: 'Tallenna',
  SAVED_PRODUCT_COLUMN_PRESETS_HEADER_TITLE:
    'Valitse hakutulokset-taulukossa näytettävät sarakkeet tallennetuista malleista',
  SAVED_PRODUCT_LIST_COLUMN_PRESETS_TITLE: 'Tallennetut sarakemallit',
  SAVED_SEARCH_EDIT: 'Tallennetun haun muokkaaminen',
  SAVED_SEARCHES_BREADCRUMBS: 'Tuotehallinta',
  SAVED_SEARCHES_TITLE: 'Tallennetut haut',
  SEARCH_FEATURE: 'Haku',
  SEARCH_FILTER_START: 'Julkaisu alkaen',
  SEARCH_FOR_PRODUCTS: 'Hae tuotteita',
  SEARCH_FROM_RESULTS: 'Etsi hakutuloksista',
  SEARCH_NAME: 'Haun nimi',
  SEARCH_PRICING: 'Etsi hinnoittelu',
  SEARCH_PRODUCTS: 'Hae tuotteet',
  SEARCH_TERMS: 'Hakuehdot',
  SEARCH: 'Hae',
  SEASON_COLORS: 'Sesongin värit',
  SEASON_INFO: 'Sesonkitiedot',
  SEASON_TYPE: 'Sesonkityyppi',
  season: 'Sesonki',
  SEASONAL_ARTICLE_CATEGORY: 'Mallisto',
  SELECT_ALL: 'Valitse kaikki',
  SELECT_DELIVERY_DAYS_FOR_PRODUCT: 'Valitse tuotteelle 1–6 toimituspäivää.',
  SELECT_DELIVERY_DAYS_HELP_TEXT:
    'Päivät järjestetään tilaukselle oikeaan aikajärjestykseen automaattisesti.',
  SELECT_DELIVERY_DAYS_FOR_PRODUCT_CENTRAL:
    'Valitse tuotteelle toimituspäivä. Keskusvaraston ostotilaukselle voi valita vain yhden toimituspäivän.',
  SELECT_DELIVERY_DAYS: 'Valitse toimituspäivät',
  SELECT_DELIVERY_DATE: 'Valitse toimituspäivä',
  SELECT_DELIVERY_DATE_DESCRIPTION:
    'Voit valita toimituspäivän, joka sopii kaikille valituille väreille.',
  SELECT_DELIVERY_DAYS_TITLE: 'Toimituspäivien valinta',
  SELECT_DELIVERY_DAYS_DESCRIPTION_ALL: 'kaikille',
  SELECT_DELIVERY_DAYS_DESCRIPTION:
    'Valitse 1-6 toimituspäivää. Valittavissa olevat päivät perustuvat värien tuotetietoihin (Pricat-sanoma). Jos olet valinnut useita eri värejä, voit valita vain sellaisia päiviä, jotka sopivat {{every}} valituille väreille. Kalenteri näyttää siis vain ne päivät, jolloin jokaisen tuotteen toimitusikkuna on samanaikaisesti avoinna.',
  SELECT_DELIVERY_DAYS_ORDER_INFO:
    'Voit lisätä uusia päiviä listaan missä järjestyksessä tahansa. Järjestelmä asettaa valitut toimituspäivät automaattisesti kronologiseen (aikajärjestyksessä nousevaan) järjestykseen riippumatta siitä, missä kohtaa listaa ne lisäät. Valitun kentän oikealla puolella näet myös automaattisesti sen viikon numeron ja vuoden, jolle päivämäärä sijoittuu.',
  DELIVERY_WEEK_DISPLAY_EMPTY: '-',
  SELECT_NEW_CAMPAIGN: 'Valitse uusi kampanja',
  SELECT_PRODUCT: 'Valitse tuote',
  SELECTED_PRODUCT: 'Valittu tuote',
  SELECT_STORE: 'Valitse kauppa',
  SELECTED_COLUMNS_NUM: 'Valittuja ominaisuuksia yht.',
  SELECTED: 'valittu',
  SELECTED_PROD: 'tuote valittu',
  SELECTED_PRODS: 'tuotetta valittu',
  SELECTED_PRODUCT_COLOR: 'tuoteväri valittu',
  SELECTED_PRODUCT_COLORS: 'tuoteväriä valittu',
  SELL_PRICE: 'OVH HINTA',
  SEND_SALES_ORDER_WAREHOUSE_ORDER_TASK_SUCCEEDED: 'Tilaus lähetetty',
  SEND_SALES_ORDER_WAREHOUSE_ORDER_TASK_FAILED: 'Tilauksen luonti epäonnistui',
  SEND_SALES_ORDER_WAREHOUSE_ORDER_TASK_CREATED: 'Tilauksen taskit luotu',
  SEND_SALES_ORDER_DIRECT_SHOP_ORDER_TASK_SUCCEEDED: 'Tilaus lähetetty',
  SEND_SALES_ORDER_DIRECT_SHOP_ORDER_TASK_FAILED: 'Tilauksen lähetys epäonnistui',
  SEND_SALES_ORDER_DIRECT_SHOP_ORDER_TASKS_CREATED: 'Tilauksen taskit luotu',
  SEND_SALES_ORDER_DIRECT_SHOP_ORDER_TASKS_CREATED_RESEND: 'Tilaukset lähetetty uudelleen',
  SEND_ALL_ORDERS_DESCR:
    'Lähetä kaikki hyväksytyt tilaukset, joita ei ole vielä lähetetty ja joissa on tuotteita',
  SEND_ORDER_TO_SAP_AND_B24: 'Lähetä tilaus SAP:iin ja B24:ään',
  SEND_ORDER_TO_SAP: 'Lähetä tilaus SAP:iin',
  SEND_ORDER: 'Lähetä tilaus',
  SENDING: 'Lähetetään',
  DELETE_PURCHASE_PARTS_DESCR:
    'Poista kaikki ostotilaukseen liittyvät osatilaukset vaihtaaksesi tilausten lähetystapaa.',
  SENT_TO_B24: 'Lähetetty B24:ään',
  SENT_TO_SAP: 'Lähetetty SAP:iin',
  SERVICE_DURATION_POINTS: 'Laskennallinen kesto',
  SERVICE_NAME: 'Palvelun nimi',
  SERVICE_PRODUCT_ASSORTMENT_ERROR:
    'Valikoimaan on linkitetty päätuotteen sijaan yhdistetty tuote.',
  SERVICE_PRODUCT_ORDER_ERROR: 'Tilaukseen on linkitetty päätuotteen sijaan yhdistetty tuote.',
  SERVICE_PRODUCT_COLOR_ERROR: 'Väri(t) on linkitetty päätuotteen sijaan yhdistettyyn tuotteeseen.',
  SERVICE_PRODUCT_VARIANT_ERROR: 'Variantti on linkitetty päävärin sijaan yhdistettyyn väriin.',
  SERVICE_PRODUCT_COLOR_ASSORTMENT_ERROR:
    'Valikoimaan on linkitetty päävärin sijaan yhdistetty väri.',
  SERVICE_PRODUCT_COLOR_ORDER_ERROR: 'Tilaukseen on linkitetty päävärin sijaan yhdistetty väri.',
  SERVICE_PRODUCT_COLOR_MISSING_SEASONS_ERROR: 'Väriltä puuttuu valikoimissa käytettyjä sesonkeja.',
  SERVICE_PRODUCT_DUPLICATE_SIZE_ERROR: 'Duplikaatti koko',
  SERVICE_PRODUCT_DUPLICATE_EAN_ERROR: 'Duplikaatti EAN',
  SERVICE_PRODUCT_DUPLICATE_SAPID_ERROR: 'Duplikaatti SAP ID',
  SERVICE_PRODUCT_INVALID_SAPID_LENGTH_ERROR: 'Virheellinen SAP ID:n pituus',
  SERVICE_PRODUCT_SAPID_MISMATCH_ERROR: 'Tuotteen ja variaation SAP ID ei täsmää',
  SERVICE_ADMIN_ERROR_PRODUCT: 'Tuotteen päivitys epäonnistui',
  SERVICE_ADMIN_ERROR_COLOR: 'Värin päivitys epäonnistui',
  SERVICE_ADMIN_FIX_ERROR: 'Tuotteen relaatioiden korjaus epäonnistui',
  SERVICE_ADMIN_FIX_ASSORTMENT_PRODUCT_ERROR:
    'Valikoiman tuotteen relaatioiden korjaus epäonnistui',
  SERVICE_ADMIN_FIX_COLOR_ERROR: 'Värin relaatioiden korjaus epäonnistui',
  SERVICE_ADMIN_FIX_ASSORTMENT_COLOR_ERROR: 'Valikoiman värien relaatioiden korjaus epäonnistui',
  SERVICE_ADMIN_FIX_VARIANT_ERROR: 'Variaation relaatioiden korjaus epäonnistui',
  SERVICE_ADMIN_PRODUCT_GET_ERROR: 'Tuotteen tietojen haku epäonnistui',
  SERVICE_ADMIN_PRODUCT_GET_NO_PARAMS_ERROR: 'Tuotekoodi, mallinumero tai SAP ID vaaditaan',
  SERVICE_ADMIN_PRODUCT_CODE_EMPTY_ERROR: 'Tuotekoodi ei saa olla tyhjä',
  SERVICE_ADMIN_PRODUCT_CODE_UNIQUE_ERROR: 'Tuotekoodin on oltava uniikki',
  SERVICE_ADMIN_PRODUCT_SAP_ID_EMPTY_WITH_ORDERS:
    'Tuotteen SAP ID ei saa olla tyhjä, koska tuotteella on tilauksia',
  SERVICE_ADMIN_PRODUCT_SAP_ID_LENGTH_ERROR: 'Tuotteen SAP ID:n on oltava 8 merkkiä pitkä',
  SERVICE_ADMIN_PRODUCT_NAME_EMPTY_ERROR: 'Tuotteen nimi ei saa olla tyhjä',
  SERVICE_ADMIN_UPDATE_IDS_ERROR:
    'Päivitys ID puuttuu. Jos ongelma toistuu, ota yhteys ylläpitoon.',
  SERVICE_ADMIN_MODEL_NUMBER_EMPTY_ERROR: 'Mallinumero ei saa olla tyhjä',
  SERVICE_ADMIN_COLOR_ID_EMPTY_ERROR: 'Valmistajan värikoodi ei saa olla tyhjä',
  SERVICE_ADMIN_COLOR_NAME_EMPTY_ERROR: 'Valmistajan värinimi ei saa olla tyhjä',
  SERVICE_ADMIN_VALID_SHIP_DATE_FROM_ERROR: 'Toimituspäivä "alkaen" ei saa olla tyhjä',
  SERVICE_ADMIN_VALID_SHIP_DATE_TO_ERROR: 'Toimituspäivä "asti" ei saa olla tyhjä',
  SERVICE_ADMIN_VALID_SHIP_DATE_FROM_BEFORE_TO_ERROR:
    'Toimituspäivän "alkaen" on oltava sama tai aikaisempi kuin "asti"',
  SERVICE_ADMIN_COLOR_SAP_CODE_EMPTY_ERROR: 'Värin SAP-koodi ei saa olla tyhjä',
  SERVICE_ADMIN_COLOR_SEASON_INVALID_ERROR: 'Värin sesonki on virheellinen',
  SERVICE_ADMIN_SIZE_EMPTY_ERROR: 'Koko ei saa olla tyhjä',
  SERVICE_ADMIN_EAN_EMPTY_ERROR: 'EAN-koodi ei saa olla tyhjä',
  SERVICE_ADMIN_B24_COLOR_MAIN_COLOR_SWITCH_VARIANTS_ERROR:
    'Ei voida yhdistää väriä toiselle päävärille, väriin on linkitetty variaatioita',
  SERVICE_ADMIN_B24_COLOR_MAIN_COLOR_SWITCH_SAP_CODE_ERROR:
    'Ei voida yhdistää väriä toiselle päävärille, jolla on eri SAP-värikoodi',
  SERVICE_ADMIN_B24_COLOR_MAIN_COLOR_SWITCH_B24_COLOR_ERROR:
    'Ei voida yhdistää väriä toiselle päävärille, koska valmistajan värikoodiin liittyy toinen SAP-värikoodi',
  SERVICE_ADMIN_COLOR_NEW_SAP_CODE: 'Uuden päävärin luominen ei ole mahdollista',
  SERVICE: 'Palvelu',
  SESSION_EXPIRED: 'Istuntosi on vanhentunut',
  SET_AS_DEFAULT: 'aseta oletukseksi',
  SET_IMAGE_SAP_IDS: 'Merkitse kuvassa esiintyvät tuotteet',
  SETTINGS: 'Asetukset',
  SHARE_LINK: 'Jaa linkki',
  SHIPMENT: 'Lähetys',
  SHOE_RANK: 'Kengät-taso',
  SHOES: 'Kengät',
  SHOE: 'Kengät',
  SHOP: 'Kauppa',
  SHOP_NAME: 'Kaupan nimi',
  SHOP_SIZE_CLASSIFICATION: 'Kaupan kokoluokitus',
  SHOW_BRAND: 'Näytä brändi',
  SHOW_SUPPLIER: 'Näytä toimittaja',
  ZERO_ROWS: 'Nollarivit',
  SHOW_ZERO_ROWS: 'Näytä nollarivit',
  SHOP_ORDER_ACCEPTED: 'Kauppa kuitannut tilauksen',
  SHORT_DESCRIPTION: 'Lyhyt kuvaus',
  SHOW_ALL_COLUMN_PRESETS: 'Näytä kaikki tallennut mallit',
  SHOW_ALL: 'Katso kaikki',
  SHOW_AS_DROP_SHIPPING_PRODUCT: 'Näytä toimitusmyyntituotteena',
  SHOW_COLORS_IN_TABLE: 'Näytä taulukossa valmistajan värit',
  SHOW_COLUMNS: 'Näytä sarakkeet',
  SHOW_ERROR: 'Näytä virhe',
  SHOW_HISTORY: 'Näytä historia',
  SHOW_INFO: 'Näytä tiedot',
  SHOW_MESSAGES: 'Näytä viestit',
  SHOW_ONLY_UNORDERED_PRODUCTS: 'Näytä vain tilaamattomat tuotteet',
  SHOW_ORDER: 'Näytä tilaus',
  SHOW_ORDER_NUMBERS: 'Näytä tilausnumerot',
  SHOW_ORDERS: 'Näytä tilaukset',
  SHOW_ORDER_CATEGORY_ROWS: 'Näytä tuotetyypit',
  SHOW_PAGE: 'Näytä sivu:',
  SHOW_RESULTS: 'Näytä tulokset',
  SHOW_SAP_DATA: 'Näytä SAP-tiedot',
  SHOW_SEARCH_RESULTS: 'Näytä hakutulokset',
  SHOW_SERVICES: 'Näytä palvelut',
  SHOW_SIDEBAR: 'Näytä haku',
  SHOW_VAT_PRICES_IN_TABLE: 'Näytä hinnat taulukossa',
  SHOW_VAT: 'Näytä alv.',
  SHOW: 'Näytä',
  SHOW_ONLY_CHANGED_PRODUCTS: 'Näytä vain muuttuneet tuotteet',
  SHOW_ORDRSP_CHANGED: 'Näytä vain muuttuneet',
  SIZE_OPEN_TO_SAP: 'Sisällytä valikoimaan',
  SIZE_SAP_ID: 'Koon SAP-numero',
  SIZE_SAP: 'SAP-koko',
  SIZE: 'Koko',
  SOMETHING_WENT_WRONG_RELOAD: 'Hups, jotain meni pieleen. Päivitä sivu, kiitos!',
  SO_DELIVERY_DATE_UPDATE_ERROR: 'Toimituspäivien muutos epäonnistui.',
  SO_DELIVERY_DATE_UPDATE_ERROR_INVALID_CHANGE:
    'Toimituspäivien muutos epäonnistui. Vaihdettu toimituspäivä on jo käytössä.',
  SO_DELIVERY_DATE_UPDATE_ERROR_PART_ORDER_EXISTS:
    'Toimituspäivien muutos epäonnistui. Tilauksia on jo luotu.',
  SO_DELIVERY_DATE_UPDATE_ERROR_INVALID_REMOVAL:
    'Toimituspäivää ei voida poistaa. Tilausrivejä on jo luotu.',
  specialPrice: 'Kampanja hinta (rajattu)',
  SPORT: 'Laji',
  sports: 'Lajit',
  SPORTS: 'Lajit',
  STAR_PRODUCT: 'Aluemarkkinoinnin kärkituote',
  START_ADDING_PRODUCTS_TO_ASSORTMENT: 'Aloita tuotteiden liittäminen valikoimaan',
  STATUS_ARCHIVED: 'Arkistoitu',
  STATUS_CANCELLED: 'Peruutettu',
  STATUS_EDITABLE: 'Muokattavissa',
  STATUS_IN_PROGRESS: 'Työn alla',
  STATUS_LOCKED: 'Lukittu',
  STATUS_NEW: 'Uusi',
  STATUS_OK: 'OK',
  STATUS_OPEN: 'Avoin',
  STATUS_PUBLISHED: 'Julkaistu',
  STATUS_READY: 'Valmis',
  STATUS_WAITING_PUBLISH: 'Odottaa julkaisua',
  STATUS: 'Status',
  stayOnPage: 'Jää sivulle',
  STORAGE_LOCATION: 'Varasto',
  STORE_MANAGER: 'Myymäläpäällikkö',
  STORE_NOTES_INFO:
    'Tähän voit kirjata esimerkiksi sovitut lisäpalvelut ja niiden hinnat, sekä muut asiakkaan kanssa erikseen sovitut asiat.',
  STORE_NOTES: 'Kaupan muistiinpanot',
  STORE_PRICINGS: 'Myymälähinnoittelu',
  storeHeight: 'Korkeus',
  storeLength: 'Pituus',
  storeWeight: 'Paino',
  storeWidth: 'Leveys',
  SUB_CATEGORY: 'Alakategoria',
  subcategory: 'Alakategoria',
  SUPPLEMENT_ORDER_BASKET_EMPTY: 'Ostoskori on tyhjä',
  SUPPLEMENT_ORDER_BASKET: 'Ostoskori',
  SUPPLEMENT_ORDER_BREADCRUMB: 'Tilaus',
  SUPPLEMENT_ORDER_CHECK_MODIFICATIONS: 'Tarkastele muutoksia',
  SUPPLEMENT_ORDER_CHECK_ORDER_DETAILS: 'Täydennä tilauksen lisätiedot.',
  SUPPLEMENT_ORDER_CONFIRM_LONG:
    'Joidenkin tuotteiden saldot eivät riittäneet. Joitain tuotteita on poistettu tai saldoja muutettu.',
  SUPPLEMENT_ORDER_CONFIRM: 'Vahvista tilaus',
  SUPPLEMENT_ORDER_CONFIRMNATION_BREADCRUMB: 'Tilausvahvistus',
  SUPPLEMENT_ORDER_CONFIRMNATION_TITLE: 'Kiitos tilauksesta!',
  SUPPLEMENT_ORDER_CONFIRMNATION:
    'Olemme vastaanottaneet tilauksesi. Tilaamasi tuotteet toimitetaan seuraavassa kuormassa toimipisteeseen.',
  SUPPLEMENT_ORDER_CREATE_INCOMPLETE: 'Anna tilauksen viite',
  SUPPLEMENT_ORDER_CREATE: 'Tee tilaus',
  SUPPLEMENT_ORDER_CREATION_FAILED: 'Tilauksen luonti epäonnistui.',
  SUPPLEMENT_ORDER_DETAILS: 'Tilaustiedot',
  SUPPLEMENT_ORDER_ESTIMATED_SHIPMENT_COSTS:
    '+ Toimituskulut (kuutioiden mukaan lasketut rahtikustannukset)',
  SUPPLEMENT_ORDER_FAILED: 'Tilaus epäonnistui',
  SUPPLEMENT_ORDER_HISTORY_ERROR: 'Tilausten haussa tapahtui virhe',
  SUPPLEMENT_ORDER_HISTORY: 'Tilaushistoria',
  SUPPLEMENT_ORDER_INSTRUCTIONS: 'Ohjeet',
  SUPPLEMENT_ORDERS_ORDER_LOADING: 'Tietoja haetaan',
  SUPPLEMENT_ORDER_N_ORDERS: 'tilausta',
  SUPPLEMENT_ORDER_ONE_ORDER: 'tilaus',
  SUPPLEMENT_ORDER_ORDER_FAILED: 'Tilaaminen ei onnistu, koska tuotteiden saldot ovat loppuneet.',
  SUPPLEMENT_ORDER_ORDERED_ITEMS: 'Tilatut tuotteet',
  SUPPLEMENT_ORDER_ORDERS: 'tilaukset',
  SUPPLEMENT_ORDER_PRODUCTS: 'Tuotteet',
  SUPPLEMENT_ORDER_RESEND: 'Lähetä tilaus uudelleen',
  SUPPLEMENT_ORDER_SEARCH: 'Tuotehaku',
  SUPPLEMENT_ORDER_SMALL_SHIPMENT_EXTRA:
    '+ 7,00 € pientoimituslisä alle 100,00 € (alv. 0 %) tilauksille',
  SUPPLEMENT_ORDER_TITLE: 'Tilaus',
  SUPPLEMENT_ORDER_USER_INFO: 'Omat tiedot',
  SUPPLEMENT_ORDERS: 'Täydennystilaukset',
  SUPPLEMENT_ORDERS_NO_USER_STORES: 'Käyttäjällä ei ole kauppoja',
  SUPPLIER_MODEL_CODE: 'Valmistajan mallinumero',
  SUPPLIER_COLOR_NAME: 'Valmistajan värinimi',
  SUPPLIER: 'Toimittaja',
  SUSTAINABILITY: 'Vastuullisuus',
  SYSTEM_MANAGEMENT: 'Hallinta',
  SYSTEM_UPDATE_AVAILABLE:
    'Uusi versio saatavilla. Klikkaa "Päivitä sivu"-painiketta varmistaaksesi, että sivusto toimii oikein.',
  SYSTEM_UPDATE_AVAILABLE_ACTION: 'Päivitä sivu',
  SWITCH_AND: 'Kaikki',
  SWITCH_OR: 'Mitkä tahansa',
  SWITCH_TITLE: 'Hakuehtojen toteutuminen',
  TARGET_GROUP: 'Kohderyhmä',
  TARGET_GROUPS: 'Kohderyhmät',
  targetgroup: 'Kohderyhmä',
  targetgroup_men: 'Miehet',
  targetgroup_women: 'Naiset',
  targetgroup_children: 'Lapset',
  targetgroup_boys: 'Pojat',
  targetgroup_girls: 'Tytöt',
  targetgroup_youth: 'Nuoret',
  TAX_CLASS: 'Veroluokitus',
  technicalInformation: 'Tekniset tiedot',
  technologies: 'Teknologiat',
  TECHNOLOGIES: 'Teknologiat',
  TEKSO_COLOR: 'Teksoväri',
  TEKSO_GROUP: 'Tekso-tavararyhmä',
  TEKSO_GROUP_FROM_SAP: 'Tavararyhmä SAP:sta',
  TEKSO_GROUP_FROM_EXISTING_PRODUCT: 'Vararyhmä tuotteelta',
  TEST_ORDER: 'TESTITILAUS',
  thumbnail: 'Kuvake',
  TO_BE_ORDERED_ITEM_AMOUNT: 'Tilattava määrä',
  TO_BE_SAP_FIXED: 'SAP-korjattavat',
  TO_CART: 'Ostoskoriin',
  TO_COLOR: 'Värille',
  TO_FIND_PRODUCTS: 'löytääksesi tuotteita.',
  TODAY: 'tänään',
  TOTAL_SUM: 'Kokonaissumma',
  TRY_AGAIN: 'Yritä uudelleen',
  UNDER_MINIMUM: 'alle min.',
  UNDO_ADD: 'Kumoa lisäys',
  UNIQUE_SAP_IDS: 'Uniikit SAP-id:t',
  UNITS: 'Määräyksiköt',
  UNSAVED_CHANGES_DIALOG_CONTENT:
    'Sivun tietoja on muokattu. Haluatko, että muutokset tallennetaan?',
  UNSAVED_CHANGES_DIALOG_TITLE: 'Sivulla on tallentamattomia muutoksia',
  UNSAVED_COLUMN_PRESET: 'Uusi sarakemalli',
  UNSAVED_SALES_ORDER_CONFIG_DIALOG_CONTENT:
    'Suosituksia ei ole tallennettu, sillä kentissä on puutteellisia tietoja.',
  SALES_ORDER_CONFIG_SAVING_IN_PROGRESS_ALERT:
    'Tallennus käynnissä. Odota, että tallennus on valmis ennen poistumista.',
  SALES_ORDER_CONFIG_SAVING_IN_PROGRESS_DIALOG_CONTENT:
    'Suositusten tallennus on kesken. Odota, että tallennus on valmis ennen poistumista.',
  SALES_ORDER_CONFIG_SAVING_COMPLETED_DIALOG_CONTENT:
    'Suositusten tallennus on valmis. Voit nyt poistua sivulta.',
  UNSAVED_SALES_ORDER_DIALOG_CONTENT:
    'Tilauksen tietoja ei ole tallennettu, sillä kentissä on puutteellisia tietoja.',
  UNSAVED_CHANGES: 'Tallentamattomia muutoksia',
  UNSAVED_CHANGES_CONFIRM: 'Sinulla on tallentamattomia muutoksia. Haluatko hylätä ne?',
  UPCOMING_MAINTENANCES: 'Tulevat varaukset',
  UPDATE_CART: 'Päivitä ostoskori',
  UPDATE_FAILED: 'Päivitys epäonnistui',
  UPDATE_SAP: 'Päivitä SAP',
  update_source: 'Päivittäjä',
  UPDATE_SUCCEED: 'Päivitys onnistui',
  UPDATE_PRODUCTS: 'Päivitä tuotteet',
  UPDATED_FROM_STAGE: 'Seuraavat tiedot päivittyvät Stagesta',
  updated: 'Päivitetty',
  WAS_UPDATED_SUCCESSFULLY: 'päivitettiin onnistuneesti',
  WAIT_FOR_LOADING_TO_EDIT_PRODUCT: 'Odota tietojen latautumista ennen tuotteen muokkaamista.',
  WILL_BE_UPDATED: 'päivitetään',
  URL_ADDRESS: 'URL-osoite',
  USE_IN_PRODUCT_LIST: 'Käytä hakutuloksissa',
  USE_OVH_PRICE_STARTING: 'Vapauta tuotteen hinnoittelu alk.',
  USE_OVH_PRICE_TEXT:
    'Tuoteryhmäalennukset tai muut hinnoittelutyypit eivät ole voimassa tuotteella, kun sen myynti on sallittu vain SAP-hinnalla.',
  USE_OVH_PRICE: 'Myynti vain SAP-hinnalla',
  USE: 'Käytä',
  USER_MAX_HEIGHT: 'Käyttäjän enimmäispituus',
  USER_MAX_WEIGHT: 'Käyttäjän enimmäispaino',
  USER_MIN_HEIGHT: 'Käyttäjän vähimmäispituus',
  USER_MIN_WEIGHT: 'Käyttäjän vähimmäispaino',
  uuid: 'Uuid',
  VALID_SHIP_DATES: 'Toimitusväli',
  VALID_SHIP_DATE_FROM: 'Toimitusväli alkaa',
  VALID_SHIP_DATE_TO: 'Toimitusväli päättyy',
  VAT_0_NET_SHORT: 'alv0% nto',
  VAT_ID: 'ALV-tunnus',
  vat: 'VAT',
  VENDOR: 'Toimittaja',
  vendor: 'Toimittajakoodi',
  VENDOR_TAX_CLASS: 'Toimittajan veroluokitus',
  VIA_B24: 'B24-palvelusta',
  VIA_FORM: 'Lomakkeella',
  VIA_YOUTUBE_VIMEO_ID: 'Youtube/Vimeo-id:llä',
  VIDEO_ID: 'Videon id',
  VIDEOS_DESCRIPTION: 'Lisää video Youtube/Vimeo-linkillä tai ID:llä.',
  VIDEOS: 'Videot',
  warehouse: 'Toimitusvarasto',
  WARRANTIES: 'Takuut',
  warranty: 'Takuu',
  WEB_PRICINGS: 'Verkkohinnoittelu',
  WEB_PRICINGS_LEGACY: 'Vanha hinnoittelu',
  SK_PRICINGS: 'Seurakauppahinnoittelu',
  WEEK_SHORT: 'vk',
  WEEK: 'vk',
  WEEK_LONG: 'Viikko',
  WHOLESALE_DISABLED:
    'Myynti seurakaupassa estetty. Poista ennakkotilaus, aktivointi- ja myyntiajastukset jos haluat tuotteen myyntiin seurakauppaan.',
  WHOLESALE_PRODUCT: 'Näkyvyys seurakaupassa',
  wholesaleProduct: 'Seurakauppatuote',
  WHICH: ', jonka',
  WHOSE: ', joiden',
  WRITE_MESSAGE: 'Kirjoita viesti',
  WITHOUT_ALV: 'ilman ALVia',
  WITH_ALV: 'ALVin kanssa',
  YES_ACCEPT: 'Kyllä, hyväksy.',
  YES_SAVE: 'Kyllä, tallenna.',
  YES_DELETE: 'Kyllä, poista',
  YES_UPDATE: 'Kyllä, päivitä tiedot',
  YES: 'Kyllä',
  YOUTUBE_ID: 'Youtube id',
  YOUTUBE_URL: 'Youtube linkki',
  YOU_ARE_EDITING: 'Olet muokkaamassa',
  BLOCK_B2B_SELLING: 'Estä myynti B2B-kaupassa',
  B2B_SELLING: 'Myynti B2B-kaupassa',
  B2B_SETTING_DISABLED: 'B2B-myynnin sallimisen/estämisen muokkaus on estetty.',
  B2B_BATCH_ITEM_TOOLTIP: 'Voit tilata myyntierällä jaollisen määrän tuotetta',
  campaignId: 'Kampanjan ID',
  campaignName: 'Kampanjan nimi',
  campaignStarts: 'Kampanja alkaa',
  campaignEnds: 'Kampanja päättyy',
  campaignType: 'Kampanjan tyyppi',
  campaignDays: 'Kampanjapäiviä yht.',
  campaignProductCount: 'Tuotteita yht.',
  campaignProductName: 'Tuotteen nimi',
  campaignProductCampaignPrice: 'Kampanjahinta',
  campaignProductColors: 'Tuotteen värit',
  campaignProductSapId: 'SAP-nro',
  campaignProductBrand: 'Brändi',
  campaignProductImage: 'Kuva',
  campaignProductActivePrice: 'Aktiivinen hinta',
  campaignProductDiscount: 'Alennus',
  campaignProductMargin: 'Kate',
  campaignProductOvhPrice: 'OVH-hinta',
  campaignProductCategory: 'Kategoria',
  campaignProductWebBalance: 'VK-saldo',
  campaignProductStoresBalance: 'Kauppojen saldo',
  campaignProductTotalBalance: 'Yhdistetty saldo',
  campaignProductSeason: 'Sesonki',
  campaignProductKwNumber: 'KW-numero',
  campaignProductSubcategory: 'Alakategoria',
  campaignProductProductType: 'Tuotetyyppi',
  campaignProductProductMainSport: 'Päälaji',
  campaignProductSapProductName: 'SAP-tuotenimi',
  campaignProductTargetgroup: 'Kohderyhmä',
  registered: 'Rekisteröity',
  cancelled: 'Peruutettu',
  shipped: 'Lähetetty',
  invalid: 'Virheellinen',
  invoiced: 'Laskutettu',
  deliverySalesProduct: 'Toimitusmyyntituote',
  deliverySalesProductSwitch: 'Tuote on toimitusmyyntituote',
  deliverySalesProductSwitchDisabled: 'Toimitusmyyntituoteen muokkaaminen on estetty.',
  seeRegulationApproval: 'Vastuullisuushyväksynnät',
  nosSeason: 'NOS-sesonki',
  nosBlockedVariants: 'Sisältää NOS-tilauksilta estettyjä variantteja',

  VARIANTS: 'Varianttia',
  VARIANT_RULE_CHANGES_SAVED: 'Muutokset tallennettu.',
  VARIANT_COUNT: 'Variantit yht.',
  PRODUCT_LEVEL_ACTIONS: 'Tuotetasolle kohdistuvat toiminnot',
  PRODUCT_LEVEL_ACTIONS_INFO:
    'Toiminnot ovat käytettävissä vasta, kun kaikki värit kaikista valituista tuotteista on valittu.',
  COLOR_LEVEL_ACTIONS: 'Väritasolle kohdistuvat toiminnot',
  COLOR_LEVEL_ACTIONS_INFO:
    'Toiminnot ovat käytettävissä ja kohdistuvat vain valittuihin väreihin.',
  MASS_EDIT_DELIVERY_DATES_SINGLE_PRODUCT:
    'Olet valinnut päivitettäväksi {{totalColors}} väriä tuotteelta {{productName}}:\n\n{{colorsList}}',
  MASS_EDIT_DELIVERY_DATES_MULTIPLE_PRODUCTS:
    'Olet valinnut päivitettäväksi {{totalColors}} väriä ({{totalProducts}} eri tuotteelta).',
  MASS_EDIT_PRODUCT_ROLE_HELP_TEXT:
    'Valitse alta uusi rooli, joka asetetaan kaikille valituille tuotteille. Valinta ylikirjoittaa tuotteiden mahdolliset aiemmat roolitiedot.',
  SERVICE_PRODUCT_TITLE: 'Valikoimanhallinnan tuotteet',
  SERVICE_PRODUCT_SEARCH_KW_NUMBER: 'Tuotekoodi(t)',
  SERVICE_PRODUCT_SEARCH_MODEL_NUMBER: 'Valmistajan mallinumero(t)',
  SERVICE_PRODUCT_SEARCH_SAP_ID: 'SAP ID(t)',
  SERVICE_PRODUCT_SEARCH_PLACEHOLDER: 'Erota pilkulla',
  SERVICE_PRODUCT_SEARCH_ERROR: 'Virhe haettaessa tuotteita:',
  SERVICE_PRODUCT_SEARCH_HINT:
    'Hae tuotteita syöttämällä tuotekoodi, valmistajan mallinumero tai SAP ID',
  SERVICE_PRODUCT_SHOWING_PRODUCTS: 'Näytetään {{count}} / {{total}} tuotetta',
  SERVICE_PRODUCT_MANUFACTURER_MODEL_NUMBER_PLURAL: 'Valmistajan mallinumero(t)',
  SERVICE_PRODUCT_MANUFACTURER_MODEL_NUMBER: 'Valmistajan mallinumero',
  SERVICE_PRODUCT_MANUFACTURER_PRODUCT_NAME: 'Valmistajan tuotenimi',
  SERVICE_PRODUCT_COLORS_COUNT: 'Värejä',
  SERVICE_PRODUCT_ASSORTMENTS_COUNT: 'Valikoimia',
  SERVICE_PRODUCT_ORDERS_COUNT: 'Tilauksia',
  SERVICE_PRODUCT_COLOR_ID: 'Värin ID',
  SERVICE_PRODUCT_SUPPLIER_COLOR_ID_PLURAL: 'Valmistajan värikoodi(t)',
  SERVICE_PRODUCT_SUPPLIER_COLOR_ID: 'Valmistajan värikoodi',
  SERVICE_PRODUCT_SUPPLIER_COLOR_NAME: 'Valmistajan värinimi',
  SERVICE_PRODUCT_SEASONS: 'Sesongit',
  SERVICE_PRODUCT_SAP_COLOR_CODE: 'SAP-värikoodi',
  SERVICE_PRODUCT_VARIANTS_COUNT: 'Variaatioita',
  SERVICE_PRODUCT_MODEL_NUMBER: 'Mallinumero',
  SERVICE_PRODUCT_VALID_SHIP_DATE_FROM: 'Toimituspäivä alkaen',
  SERVICE_PRODUCT_VALID_SHIP_DATE_TO: 'Toimituspäivä asti',
  SERVICE_PRODUCT_VARIANT_ID: 'Variaation ID',
  SERVICE_PRODUCT_DELETED_SUCCESS: 'Poistettu onnistuneesti',
  SERVICE_PRODUCT_DELETE_FAILED: 'Poisto epäonnistui',
  SERVICE_PRODUCT_SEARCH_FAILED: 'Haku epäonnistui',
  SERVICE_PRODUCT_FIXED_SUCCESS: 'Korjattu onnistuneesti',
  SERVICE_PRODUCT_FIX_FAILED: 'Korjaus epäonnistui',
  SERVICE_PRODUCT_SAVE_FAILED_WITH_ERROR: 'Tallennus epäonnistui: {{error}}',
  SERVICE_PRODUCT_SAVE_FAILED: 'Tallennus epäonnistui',
  SERVICE_PRODUCT_MOVE_FAILED:
    'Siirto epäonnistui. Korjaa ensin mahdolliset linkitysvirheet tuotteelta ja yritä uudelleen. Jos ongelma ei korjaannu, ota yhteyttä järjestelmän ylläpitoon',
  SERVICE_PRODUCT_CONFIRM_CANCEL_EDIT: 'Peruuta muokkaus',
  SERVICE_PRODUCT_CONFIRM_SAVE_EDIT: 'Tallenna muokkaus',
  SERVICE_PRODUCT_CONFIRM_MOVE_RELATIONS: 'Siirrä valikoimien ja tilausten linkitykset',
  SERVICE_PRODUCT_CONFIRM_UNSAVED_CHANGES: 'Tallentamattomia muutoksia',
  SERVICE_PRODUCT_CONFIRM_DELETE_ROW: 'Poista rivi',
  SERVICE_PRODUCT_CONFIRM_CANCEL_EDIT_TEXT:
    'Haluatko varmasti peruuttaa muokkauksen? Tallentamattomat muutokset menetetään.',
  SERVICE_PRODUCT_CONFIRM_DELETE_ROW_TEXT:
    'Haluatko varmasti poistaa rivin? Tätä toimintoa ei voi peruuttaa.',
  SERVICE_PRODUCT_CONFIRM_MOVE_RELATIONS_TEXT:
    'Haluatko varmasti siirtää valikoimien ja tilausten linkitykset tähän variaatioon?',
  SERVICE_PRODUCT_CONFIRM_UNSAVED_CHANGES_TEXT:
    'Sinulla on tallentamattomia muutoksia. Haluatko hylätä muutokset ja hakea uudelleen?',
  SERVICE_PRODUCT_CONFIRM_SAVE_EDIT_TEXT:
    'Muutokset periytyvät myös alatasoille ja muihin linkitettyihin tauluihin, kuten valikoimiin. Haluatko varmasti tallentaa muutokset?',
  SERVICE_PRODUCT_CONFIRM_SAVE_EDIT_TEXT_VARIANT: 'Haluatko varmasti tallentaa muutokset?',
  SERVICE_PRODUCT_TOOLTIP_FIX_ERRORS: 'Korjaa linkitysvirheet',
  SERVICE_PRODUCT_TOOLTIP_HAS_ERRORS: 'Sisältää virheitä',
  SERVICE_PRODUCT_TOOLTIP_DELETE_ROW: 'Poista rivi',
  SERVICE_PRODUCT_TOOLTIP_MOVE_RELATIONS:
    'Siirrä linkitetyt valikoimat ja tilaukset toiselle variaatiolle',
  SERVICE_PRODUCT_CHIP_MOVING_RELATIONS: 'Siirretään linkitykset',
  SERVICE_PRODUCT_TOOLTIP_CANCEL_MOVE: 'Peruuta siirto',
  SERVICE_PRODUCT_BUTTON_MOVE_TO_VARIANT: 'Siirrä tälle variaatiolle',
  SERVICE_PRODUCT_TOOLTIP_EDIT_ROW: 'Muokkaa riviä',
  SERVICE_PRODUCT_TOOLTIP_SAVE_CHANGES: 'Tallenna muutokset',
  SERVICE_PRODUCT_TOOLTIP_CANCEL_EDIT: 'Peruuta muokkaus',
  SERVICE_PRODUCT_HEADING_COMBINED_MODEL_NUMBERS: 'Yhdistetyt mallinumerot',
  SERVICE_PRODUCT_HEADING_PRODUCT_COLORS: 'Tuotteen värit',
  SERVICE_PRODUCT_HEADING_COMBINED_COLORS: 'Yhdistetyt värit',
  SERVICE_PRODUCT_HEADING_COLOR_VARIANTS: 'Värin variaatiot',
} as const;

export { LOCALE_FIN };
